import { CAMPOS_POR_SISTEMA, cumpleDependencia, compilarSistemaDetalle } from './escalasEvolucion';
import { espejarCompartidos } from './datosCompartidos';
import { SISTEMAS } from './evolucionSistema';
import { esPosteriorA, ordenarPorHecho, momentoDeEntrada } from './momento';

// ── "Dilo cuando pase": voz → campos de la evolución, sin IA ──────────────────
//
// Tercer parser DETERMINISTA de la casa, hermano de `parseAccionDictada`
// (registroTurno.js) y `parseExamenDictado` (examenFisico.js), y con sus MISMAS
// garantías duras: cues `[label, regex]` alineadas 1:1 con las `opciones` del
// catálogo (el label emitido ES el string de `CAMPOS_POR_SISTEMA`, jamás texto
// re-escrito), tramos por sistema para que una palabra compartida no contamine
// al vecino, guarda de negación, y el ASR crudo NUNCA aterriza en un campo.
// Catálogo cerrado + números ⇒ PII-safe por construcción.
//
// El PORQUÉ (respuesta del fundador, 2026-08-16): en la visita de la mañana
// todavía no sabe cómo está la orina, si hubo deposiciones, si hizo fiebre ni
// qué dijeron los exámenes de sangre. Los va sabiendo DURANTE el día. Por eso
// esos datos no se preguntan al recibir (el chequeo general no se engorda), se
// dicen en el momento en que pasan y caen solos a la evolución.
//
// Alcance deliberadamente ANGOSTO (mejor certero que ancho que adivine): los
// cuatro sistemas que ningún puente puede sembrar (renal, gastro, infecto,
// hemato) y, dentro de ellos, lo que se dice de verdad en un turno. Lo que
// quedó fuera y por qué está anotado en FUERA_DE_ALCANCE, al final del archivo.
//
// LA REGLA QUE MANDA SOBRE TODAS (auditoría clínica del 2026-08-16): ante la
// duda, NO se escribe el campo. Un campo en blanco lo llena la enfermera en dos
// toques; un campo escrito al revés (fiebre que no hubo, tromboprofilaxis que
// se suspendió, transfusión que solo se pidió) queda en el documento como si
// hubiera pasado. Todo lo que no se reconoce cae como nota verbatim, así que
// nada de lo dicho se pierde por ser estricto acá.

// Normalizador local (minúsculas, sin diacríticos, espacios colapsados). Sí, es
// el mismo de sus dos hermanos: la deuda del normalizador duplicado está
// anotada en el cierre de la ola, y unificarla ahora tocaría tres parsers.
const _norm = (t) => String(t || '')
  .toLowerCase()
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/\s+/g, ' ')
  .trim();

// Número dictado: acepta miles con punto (1.500) y decimal con coma (8,2), que
// es como se dicen y como el ASR los escribe en Chile. La salida se guarda como
// STRING con punto decimal, idéntica a lo que deja el input numérico de la Mesa
// (misma superficie, mismo dato: no se inventa un formato paralelo).
const NUM = '(-?\\d{1,3}(?:\\.\\d{3})+|-?\\d+(?:[.,]\\d+)?)';

function _aNumero(s) {
  const t = String(s || '').trim();
  const n = /^-?\d{1,3}(\.\d{3})+$/.test(t)
    ? Number(t.replace(/\./g, ''))
    : Number(t.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

// Ventana corta entre la palabra clave y su número, que NO cruza puntuación:
// "hgt 145" y "balance de 450" caen; "hgt normal, pcr 180" no le presta el 180
// al HGT (y además viven en tramos distintos). El HUECO se captura (grupo 1)
// porque hay que revisarlo: ver CORTE_NUM.
const reNum = (prefijo, hueco = 16) => new RegExp(`${prefijo}([^\\d,;:.\\n]{0,${hueco}})${NUM}`);

// El hueco entre la palabra clave y su número no puede contener OTRO analito ni
// una conjunción que cambie de sujeto. Bug real de la auditoría: "hemoglobina y
// plaquetas 90" dejaba `hb: 90` y el documento imprimía "Hb 90 g/dL" con un
// valor que era de las plaquetas. Si el hueco cambia de sujeto, el número no es
// del primer analito y no se escribe.
//
// Los marcadores de HORA son de la misma familia y faltaban: "hgt de las 14 fue
// 110" documentaba una glicemia de 14, que es una hipoglicemia severa que nadie
// tuvo, y el 110 verdadero ni se veía. El caso peligroso es justo el que cae
// DENTRO del rango, porque el rango existe para que un HGT de 14 sea creíble.
const CORTE_NUM = /\b((a|de) las|hora(s)?|y|e|o|con|mas|luego|despues|ademas|pero|plaquetas?|globulos?|blancos?|ran|leucocitos|neutrofilos|hb|hemoglobina|hgt|glicemia|hemoglucotest|pcr|procalcitonina|lactic\w*|lactato|creatinina|bun|urea|sodio|potasio|inr|ttpa|bilirrubina|balance|diuresis|residuo|debito|temperatura|presion|saturacion|frecuencia)\b/;

// Rangos plausibles por campo numérico. NO son cortes clínicos (esos viven en
// las `bandas` del catálogo, y ahí se interpretan): son los márgenes ANCHOS de
// lo que un valor PUEDE llegar a ser en la vida real, para que un error del ASR
// no aterrice un absurdo en el documento. Casos reales de la auditoría: "hgt
// 1.450" (era 145) y "lactato 40" (era 4,0). Fuera de rango NO se escribe: el
// fail-safe es siempre hacia no inventar, y lo dicho igual queda como nota.
// OJO con los pisos (2ª auditoría): el rango ataja el absurdo del ASR, NO hace
// de corte clínico. Un HGT de 18 es real, urgente y justo el número que más
// importa documentar: con el piso en 20 se perdía en silencio. Cuando un número
// dicho se descarta, el preview lo AVISA (ver `descartados`).
const RANGOS = {
  // Glicemia capilar en mg/dL: el glucómetro informa LO/HI fuera de ~10 a 600,
  // y la hipoglicemia severa vive justo en el piso.
  hgt: [10, 600],
  // PCR en mg/L: los valores muy altos rondan 400 a 500; 600 deja margen.
  pcr: [0, 600],
  // Procalcitonina en ng/mL: los ensayos saturan cerca de 100.
  procalcitonina: [0, 200],
  // Láctico en mmol/L: sobre 20 ya es excepcional; 30 es margen generoso.
  lactico: [0, 30],
  // Hb en g/dL: el piso solo ataja el 0 y los negativos; una Hb de 2,5 se
  // transfunde, no se descarta.
  hb: [1, 25],
  // Día de tratamiento antibiótico.
  atbDia: [1, 60],
  // Balance del turno en ml, con signo. ±15 L cubre incluso un politraumatizado.
  balance: [-15000, 15000],
  // Días sin tránsito intestinal.
  depositosDias: [1, 30],
};

const _enRango = (k, n) => {
  const r = RANGOS[k];
  return !r || (n >= r[0] && n <= r[1]);
};

// ── Tramos por sistema ────────────────────────────────────────────────────────
// Cada pista abre el tramo de su sistema y corre hasta la siguiente pista (de
// cualquier sistema). Sin esto, "clara" de la orina o "presentes" de las
// deposiciones se buscarían en todo el dictado y contaminarían al vecino.
// A diferencia del examen, el tramo se extiende hacia ATRÁS hasta el inicio de
// su cláusula (sin cruzar la marca anterior): así "quedó afebril, sin
// aislamiento" deja el "sin" DENTRO del tramo del aislamiento, que es donde la
// guarda de negación lo necesita. Mismo criterio de "la misma cláusula" que ya
// usa parseAccionDictada para lateralizar una zona.
const PISTAS = {
  renal: [/\bdiuresis\b|\borina\b|\borino\b|\bbalance\b|oligur|anuri|poliuri|\bfoley\b|cateter urinario|sonda vesical|sonda urinaria|sondeo intermitente|urostom|hematuri|coluri|piuri|\bchata\b|\bpanal\b/],
  gastro: [/\bregimen\b|alimentacion (oral|enteral|parenteral)|nutricion (enteral|parenteral)|\bnpt\b|\bingesta\b|rechazo alimentario|\bdeposicion(es)?\b|\btransito\b|\bobro\b|\bdefeco\b|\bhgt\b|glicemia|hemoglucotest|\bvia oral\b|nada por boca|\bnpo\b|\bdiarrea\b/],
  // OJO con los bordes: la pista es \bfebril (sin cerrar), porque "peaks
  // febriles" en plural no abriría su tramo con \bfebril\b y el estado febril
  // se perdería. Y "parametro" a secas NO abre tramo de infecto: "parámetros
  // hemodinámicos estables" (hemodinamia, otro sistema) aterrizaba como
  // "parámetros inflamatorios estables" en Infectología (bug real de la
  // auditoría). El adjetivo ES el ancla.
  infecto: [/\bfebril|\bfiebre|\bafebril|febricula|subfebril|cultivo|aislamiento|parametros (inflamatorios|infecciosos)|\bpcr\b|procalcitonin|\blactic|\blactato\b|\batb\b|antibiotic/],
  // "llegaron los examenes" es cue de `labHemato` y tiene que abrir su tramo:
  // sin la pista, la rama del laboratorio y su Hb se caían enteras (3ª
  // auditoría). Una cue sin pista que la abra es una cue muerta.
  hemato: [/\bcni\b|tromboprofilaxis|anticoagul\w*|\btaco\b|clexane|enoxaparina|\bhbpm\b|\bhnf\b|heparina|medias antitromb|compresion neumatica|botas neumaticas|transfus|transfund|sangrado|sangramiento|\bsangra\b|hemoglobina|\bhb\b|laboratorio|llegaron los examenes|examenes de (sangre|hoy|la manana)/],
  // ── Los tres sistemas del episodio crítico (R3 del plan, 2026-08-23) ──────
  // El dictado real del fundador terminaba en "está conectado a vmi en modo vc
  // con peep de 5" y caía como nota: la voz no conocía ventilación, neuro ni
  // hemodinamia. Mismas reglas de la casa: cada cue mapea 1:1 a un valor del
  // catálogo (CAMPOS_POR_SISTEMA), negadores con \b, y ante la ambigüedad,
  // silencio con aviso.
  //
  // OJO con tres trampas que se cazaron al escribir las pistas:
  //  - 'conectad' NO es pista de neuro: "conectado a VMI" abriría un tramo de
  //    conciencia sobre una frase de ventilador.
  //  - \bsopor\b con borde: "soporte" no puede abrir el tramo de conciencia.
  //  - 'mascarilla' exige su apellido (simple/Venturi/reservorio…): la
  //    "bolsa-mascarilla" del rescate de intubación no es oxigenoterapia.
  ventilacion: [/via aerea|\bvmi\b|ventilacion mecanica|ventilador|\bpeep\b|\bcnaf\b|alto flujo|\bvni\b|bipap|\bcpap\b|oxigenoterapia|naricera|mascarilla (simple|venturi|de (alto flujo|reservorio|recirculacion))|aire ambiente|aire ambiental|tubo orotraqueal|\btqt\b|traqueostom|modo (vc|pc|volumen|presion|espontaneo)/],
  // Los nombres del catálogo de sedoanalgesia también abren el tramo (2026-09-08):
  // "se suspende propofol" a secas no tenía pista y caía a nota, así que el
  // fármaco seguía escrito como vigente (caso L de la evaluación).
  neuro: [/sedacion|sedad[oa]\b|sedoanalgesia|\bvigil\b|despiert[oa]\b|somnolient|\bsopor\b|sopor (superficial|profundo)|propofol|midazolam|dexmedetomidina|precedex|\bfentanil\w*|remifentanil\w*|\bketamina\b|esketamina|\bmorfina\b/],
  hemodinamia: [/hipotens|normotens|hipertens|bradicard|taquicard|normocard|ritmo sinusal|fibrilacion|drogas? vasoactiv|vasoactiv|\bdva\b|noradrenalina|\bnad\b|bien perfundid|hipoperfusion|mal perfundid/],
};

// Vetos por sistema: contextos donde el vocabulario del sistema significa OTRA
// cosa. La heparina del CIRCUITO de diálisis (renal, `trrAnticoag`) no es
// tromboprofilaxis del paciente, y confundirlas escribiría en la evolución que
// está anticoagulado cuando solo lo está el filtro. Ante la duda, el tramo se
// descarta y el dictado cae como nota: sub-matchear es más seguro que falsear.
const VETOS = {
  hemato: /circuito|\btrrc\b|dialisis|\bhdi\b|\bhdvvc\b|citrato|filtro/,
};

function _spans(norm) {
  const marcas = [];
  for (const [sis, regexes] of Object.entries(PISTAS)) {
    for (const re of regexes) {
      const g = new RegExp(re.source, 'g');
      let m;
      while ((m = g.exec(norm)) !== null) {
        marcas.push({ sis, idx: m.index });
        if (m.index === g.lastIndex) g.lastIndex += 1; // guarda ante match vacío
      }
    }
  }
  marcas.sort((a, b) => a.idx - b.idx);
  return marcas.map((m, i) => {
    const fin = i + 1 < marcas.length ? marcas[i + 1].idx : norm.length;
    // La extensión hacia atrás se detiene en la marca anterior, en el signo de
    // puntuación o a 40 caracteres: sin ese tope, una frase larga sin comas
    // dejaría al tramo de un sistema leyendo el texto del vecino.
    const piso = Math.max(i > 0 ? marcas[i - 1].idx : 0, m.idx - 40);
    let ini = m.idx;
    while (ini > piso && !/[,;.:]/.test(norm[ini - 1])) ini -= 1;
    // `ini` viaja con el tramo: las guardas y la posición se calculan sobre el
    // norm completo, así que hay que poder volver de la coordenada del tramo a
    // la absoluta.
    return { sis: m.sis, texto: norm.slice(ini, fin), ini };
  });
}

// ── La cláusula: la unidad de todo lo que sigue ───────────────────────────────
// Las guardas y la resolución por posición NO se calculan sobre el tramo del
// sistema, sino sobre el texto COMPLETO. Razón (bug real de la 2ª auditoría):
// cuando la misma pista dispara dos veces, el segundo tramo arranca EN la marca
// anterior, así que empieza con la palabra clave y "se retiró" quedaba fuera:
// "se retiró la sonda vesical y quedó con chata" documentaba el catéter puesto.
// El tramo sigue aislando QUÉ cues se buscan; la cláusula decide el sentido.
//
// Límite de cláusula = la puntuación real MÁS las marcas de RE-PREDICACIÓN
// ("y quedó", "y ahora", "pero está"), porque así se habla sin comas: "se
// suspendió el clexane y quedó con medias" son dos hechos, no uno.
const RE_PREDICACION = /\b(y|e|pero|luego|despues|entonces)\s+(quedo|queda|quedaron|ahora|esta|estan|se dejo|se instalo|se cambio|se paso|paso a|continua|sigue|siguio)\b/;

function _cortes(norm) {
  const inicios = [0];
  for (let i = 0; i < norm.length; i += 1) if (/[,;.:]/.test(norm[i])) inicios.push(i + 1);
  const g = new RegExp(RE_PREDICACION.source, 'g');
  let m;
  while ((m = g.exec(norm)) !== null) {
    inicios.push(m.index);
    if (m.index === g.lastIndex) g.lastIndex += 1;
  }
  return [...new Set(inicios)].sort((a, b) => a - b);
}

// Índice de la cláusula que contiene `idx` (posición ABSOLUTA en el norm).
const _clausulaDe = (ctx, idx) => {
  let i = 0;
  while (i + 1 < ctx.cortes.length && ctx.cortes[i + 1] <= idx) i += 1;
  return i;
};

// ── Las guardas que deciden el SENTIDO de una cue ─────────────────────────────
// Lo que se dijo justo antes del match, dentro de su cláusula.
const _antes = (ctx, idx) => ctx.norm.slice(ctx.cortes[_clausulaDe(ctx, idx)], idx);

// EL ALCANCE DE LA NEGACIÓN, que costó CUATRO intentos, y la salida no fue un
// quinto alcance: fue dejar de resolver.
//
// El problema real es que "sin fiebre y deposiciones" es AMBIGUO de verdad:
// puede ser "sin fiebre y [sin] deposiciones" (enumerar ausencias sin repetir el
// negador es como se habla en una entrega) o "sin fiebre y [con] deposiciones".
// Los cuatro intentos anteriores eligieron un lado: gobernar toda la cláusula
// tapaba el segundo hecho, mirar tres palabras invertía el hallazgo con
// cualquier complemento de por medio, y cortar en el coordinante resolvía hacia
// el lado AFIRMATIVO, que es lo contrario de la doctrina escrita arriba de este
// archivo. Cambiar una pérdida avisada por una inversión muda es peor.
//
// La regla, entonces, es la misma que ya rige las colisiones y que `anclarCuracion`
// de pielLpp.js tomó antes que nosotros:
//   · negador PEGADO (sin coordinación de por medio) ⇒ negación clara: se veta.
//   · negador con una coordinación en medio ⇒ AMBIGUO: la cue no se escribe ni
//     se descarta a ciegas. Si hay otro valor vivo del mismo campo, el campo
//     entero queda en blanco y se informa como colisión; si no, se informa como
//     descartado. Nunca se elige por ella.
// "Ni" no coordina en este sentido, reparte la negación: "sin fiebre ni
// deposiciones" niega las dos y por eso NO abre ambigüedad.
const COORDINA = /\s(?:y|e|o|pero)\s/;
const _ambitoNegacion = (antes) => antes.split(COORDINA).pop();

// null · 'negado' (clara) · 'negado-coord' (ambigua por coordinación).
//
// El ADVERSATIVO no reparte: "sin fiebre pero con deposiciones" es inequívoco,
// el "pero" CIERRA la negación igual que una coma. Solo "y", "e" y "o" abren
// duda. Ojo: sacar "pero" de la lista de coordinantes sería peor que dejarlo,
// porque entonces el ámbito no se cortaría ahí y la negación pasaría a vetar
// duro lo que viene después.
function _negacion(antes, propia) {
  if (propia) return null;
  const ultimo = [...antes.matchAll(/\s(y|e|o|pero)\s/g)].pop();
  const ambito = ultimo ? antes.slice(ultimo.index + ultimo[0].length) : antes;
  if (NEGACION.test(ambito)) return 'negado';
  if (!ultimo || ultimo[1] === 'pero') return null;
  return NEGACION.test(antes) ? 'negado-coord' : null;
}

// Y lo que se dijo justo después, dentro de la misma cláusula: cuatro palabras,
// que es lo que alcanza un complemento pegado al sustantivo. Lo usan el veto
// propio de una cue (VETO_HEPARINA) y el participio de retiro.
const PALABRAS_DESPUES = 6;
const _despues = (ctx, idx, largo) => {
  const fin = ctx.cortes[_clausulaDe(ctx, idx) + 1] ?? ctx.norm.length;
  return ctx.norm.slice(idx + largo, fin).split(' ').slice(0, PALABRAS_DESPUES + 1).join(' ');
};

// 1) NEGACIÓN. Acá SÍ se veta el "no", al revés que en `parseExamenDictado`, y
// la diferencia no es un descuido: en el examen "no blanquea" ES el hallazgo
// (el catálogo tiene la opción negativa y describirla afirma algo), mientras
// que en estos cuatro sistemas el catálogo describe estados presentes: "no hizo
// fiebre" NO es "Peaks febriles", "no tiene sangrado activo" NO es "Con
// sangrado activo" y "no hay aislamiento de contacto" NO es aislamiento de
// contacto. Los tres eran bugs reales. `ningun` y "nada de" son negadores tan
// normales como "sin" en la boca de una enfermera ("sin ningún signo de
// sangrado activo") y faltaban.
const NEGACION = /\b(sin|no|ni|niega|nunca|tampoco|jamas|ningun\w*|nada de)\b/;

// 2) RETIRO / SUSPENSIÓN. "Se suspendió el clexane" documentaba tromboprofilaxis
// farmacológica PUESTA, o sea justo lo contrario de lo que pasó. Lo retirado no
// se afirma; si de verdad hay que dejar constancia, la nota verbatim ya la lleva
// y la enfermera elige el campo. Esta lista y la de participios de más abajo
// nombran los MISMOS verbos: si divergen, "se extrajo la sonda" pasa por un lado
// y no por el otro (pasó, 4ª auditoría).
const RETIRO = /\b(suspend\w*|suspensi\w*|retir\w*|saco|sacaron|sacamos|quit\w*|discontinu\w*|descontinu\w*|extraj\w*|extrae|omiti\w*|baj\w*|disminuy\w*|se dejo)\b/;

// 3) PLAN. Invariante fechada del repo (2026-07-30): un plan NO es un hecho. Lo
// pedido, indicado o pendiente no se documenta como ocurrido sin constatación,
// y acá no hay ninguna red aguas abajo que lo ataje: este parser escribe directo
// al documento. "Se pidió transfusión de plaquetas" y "quedó pendiente la
// transfusión" registraban la transfusión como hecha.
// El regex estaba construido sobre "se" + verbo, así que las formas en primera
// persona ("pedí hemocultivos") y las perifrásticas ("queda para transfusión",
// "en espera de") se colaban y firmaban en el documento un cultivo que nadie
// tomó (4ª auditoría).
const PLAN = new RegExp([
  '\\bpendiente\\w*\\b', '\\bindicad\\w*\\b',
  '\\bse (pidio|pide|solicito|solicita|indico|indica|programo|programa|pidieron|solicitaron)\\b',
  '\\b(pedi|pedimos|solicite|solicitamos|deje (pedido|solicitado|indicado))\\b',
  // Las perifrásticas de futuro y de obligación, en todas sus personas: "le voy
  // a tomar", "tengo que tomar", "hay que instalar" describen lo que falta
  // hacer, no lo hecho.
  '\\b(se |)va(n|) a\\b', '\\b(voy|vamos|vas) a\\b',
  '\\b(hay|tengo|tenemos|tienes) que\\b',
  '\\ble corresponde\\b', '\\bme toca\\b', '\\besperando\\b',
  '\\b(debo|debe|debemos|deben)\\b', '\\bfalta(n|)( por|) (tomar|instalar|pasar|hacer|transfundir|iniciar|indicar|controlar)\\b',
  '\\bindicacion de\\b',
  '\\b(a|en) (la |)espera de\\b',
  '\\bqueda(n|) (para|pendiente)\\b', '\\bquedo (citado|programado|agendado) para\\b',
  '\\bpor (indicar|iniciar|tomar|pasar)\\b', '\\bse (tomara|iniciara|pasara|hara|evaluara|instalara|transfundira|indicara|dejara)\\b',
].join('|'));

// El plan también se dice DESPUÉS, como complemento de tiempo futuro pegado al
// sustantivo ("transfusión de plaquetas para la tarde", "régimen cero a partir
// de mañana"). Mismo principio de adyacencia que el participio de retiro: el
// complemento califica a lo que va justo antes, no a la frase entera.
// Va sin anclar al inicio (a diferencia del participio) porque el complemento
// puede colgar del sustantivo y no del match: la cue de transfusión termina en
// "transfusión de" y el "para la tarde" viene tras el hemocomponente.
//
// OJO, y es la diferencia que costó un rojo: "para" introduce en clínica dos
// cosas distintas, y solo una es un plan. El complemento de FINALIDAD es
// constante en cómo se habla ("sonda foley para el control de diuresis",
// "clexane para la profilaxis", "aislamiento de contacto para la klebsiella")
// y describe algo que YA está puesto; el de TIEMPO FUTURO ("para la tarde",
// "a partir de mañana") es el que anuncia lo que todavía no pasa. Con el
// genérico se perdían cuatro frases reales, y en silencio.
const DIAS_SEMANA = 'lunes|martes|miercoles|jueves|viernes|sabado|domingo';
const PLAN_POSTERIOR = new RegExp(`\\b(para (la (tarde|noche|manana)|manana|hoy|el (proximo )?turno|el turno de (noche|tarde|manana)|(el |los |)(${DIAS_SEMANA})|las \\d{1,2}(:\\d{2})?)|a partir de|desde manana|en la (tarde|noche) de manana)\\b`);

// ── LA SALIDA ESTRUCTURAL PENDIENTE (anotada, no hecha) ──────────────────────
// `PLAN` y `ANTICIPATORIO` son LISTAS NEGRAS abiertas: cada auditoría encontró
// formas nuevas de anunciar algo que todavía no pasa ("pedí", "queda para", "me
// toca", "falta tomar", "está programada", "se instalará"), y cada ronda las
// agregó a mano. Mientras sea una lista, siempre va a faltar una.
//
// La salida simétrica a lo que ya hicimos con la negación existe y se llama
// igual: un verbo en FUTURO o en perífrasis de obligación, sin constatación en
// la misma cláusula, deja el campo en DUDA (no se escribe, se avisa), igual que
// la coordinación. Sería el mismo mecanismo `dudoso` que ya está construido.
//
// NO se implementó a propósito (decisión del fundador, 6ª auditoría): llevamos
// seis rondas y cada regla estructural nueva generó su error espejo en la ronda
// siguiente. Abrir un frente nuevo acá es cómo se sigue girando. Queda escrito
// para quien la retome: el mecanismo ya existe, lo que falta es detectar el
// tiempo verbal sin una lista, y hay que medirlo contra el corpus versionado
// (`__fixtures__/dictadoCorpus.js`) ANTES de cambiar nada.
//
// Dos casos conocidos que esa salida cerraría, y que hoy pierden el hecho CON
// aviso (frecuencia baja, se dejan a propósito): "esperando resultado del
// hemocultivo" (el cultivo se tomó, lo que se espera es el resultado) y "me
// toca el turno de noche y orina clara" (el marcador de plan no habla de la
// orina). Los dos son el mismo problema: la lista negra no distingue a qué
// parte de la frase se refiere el plan.

// 4) LO ANTICIPATORIO, que es la otra mitad de lo que se dicta en un turno:
// "vigilar diuresis por si hace oliguria", "avisar si hay sangrado activo",
// "en caso de fiebre tomar hemocultivos". Nada de eso ocurrió, y sin esta
// guarda un cultivo que jamás se tomó quedaba en el documento como tomado.
//
// Bloquea lo que viene DESPUÉS de él, no lo anterior, y la posición es la del
// texto, no la de una coma: lo que se afirmó antes de decir "si" o "vigilar" es
// un hecho constatado, y lo que viene después es lo hipotético. En "orina clara
// y si hace fiebre avisar" la orina se escribe y la fiebre no. La primera
// versión bloqueaba el dictado entero y se comía lo ya dicho.
//
// OJO con el "si": `_norm` quita las tildes, así que el "sí" de afirmación y el
// "si" condicional colapsan en la misma palabra. Solo cuenta como condición
// cuando INTRODUCE una (va seguido de verbo o de "no"): "sí, quedó afebril"
// escribe Afebril; "si hace fiebre avisar" bloquea.
const SI_CONDICIONAL = 'si (no |se )?(hace|hay|tiene|presenta|aparece|sube|baja|llega|persiste|vuelve|sigue|empeora|cae|sangra|obra|obro|queda|quedo|pasa|pone|repite|mantiene|desatura|convulsiona)';
const ANTICIPATORIO = new RegExp(`\\b(${SI_CONDICIONAL}|si no\\b|en caso de|vigilar|controlar|estar (atenta|atento|pendiente)|ojo con|avisar|riesgo de|preguntar|por si|manana (se|le|lo|hay|toca|va|van)|se (toma|toman|inicia|inician|instala|instalan))\\b`, 'g');

// "Mañana" el día y "la mañana" el rato del turno son la misma palabra para el
// normalizador, y confundirlas costaba caro: "en la mañana se tomó hemocultivo"
// se leía como algo por hacer y los dos cultivos no se escribían, sin avisar.
// Si delante viene un artículo, es el rato del turno y no anticipa nada.
const MANANA_DEL_TURNO = /(la|esta|una)\s+$/;

// Dónde empieza lo hipotético EN CADA CLÁUSULA (no en todo el dictado).
function _anticipaciones(norm, ctx) {
  const mapa = new Map();
  const g = new RegExp(ANTICIPATORIO.source, 'g');
  let m;
  while ((m = g.exec(norm)) !== null) {
    if (m.index === g.lastIndex) g.lastIndex += 1;
    if (m[0].startsWith('manana') && MANANA_DEL_TURNO.test(norm.slice(0, m.index))) continue;
    const c = _clausulaDe(ctx, m.index);
    if (!mapa.has(c)) mapa.set(c, m.index);
  }
  return mapa;
}

// 5) LO RETIRADO DICHO DESPUÉS ("sonda foley retirada"). Con la guarda hacia
// atrás sola, el dictado documentaba como PUESTO lo que acababa de retirarse.
// El principio es gramatical, igual que el de la negación: un verbo de retiro
// puede retractar una COSA, jamás una observación de estado. "Retirada" se dice
// de una sonda, de un drenaje o de un catéter; no se dice de que la orina esté
// clara ni de que haya hecho fiebre. Por eso:
//   (a) solo se aplica a los campos que el catálogo MARCA como cosa retirable
//       (abajo), nunca a los que describen un estado observado, y
//   (b) el participio modifica al sustantivo que va JUSTO antes, así que se
//       mira la palabra siguiente al match y no una ventana de N caracteres ni
//       la cláusula entera: "aislamiento de contacto y cvc retirado" retira el
//       CVC, no el aislamiento.
// El prefijo opcional acepta el relativo, porque el castellano lo intercala
// todo el tiempo ("la sonda foley QUE FUE retirada") y sin él la guarda no veía
// nada mientras la de hacia atrás tampoco, porque el verbo va después.
// "Programada" sola no dice si ya pasó: "se realizó la transfusión programada"
// es un hecho y "dejó programada la transfusión" es un plan. El parser NO lo
// resuelve (no intentamos leer el tiempo verbal), pero tampoco puede avisar
// "lo dijiste como algo por hacer" sobre una frase en pretérito: eso sería
// afirmar lo contrario de lo que ella dijo. Motivo propio y frase honesta.
const PROGRAMADO = /\bprogramad\w*\b/;

const RETIRO_PARTICIPIO = /^\s*(que (ya |)(fue|habia sido|esta|quedo) |ya |fue |esta |quedo )?(suspendid|retirad|sacad|discontinuad|descontinuad|extraid)\w*/;

// Campos cuya cue nombra un dispositivo, un soporte o una indicación, o sea algo
// que se puede retirar. Se declara acá en vez de inferirlo del texto: la lista
// es corta, es clínica y se lee de una.
const CAMPOS_RETIRABLES = {
  renal: ['diuresisVia'],
  gastro: ['regimen', 'regimenOral'],
  infecto: ['aislamiento'],
  hemato: ['tromboprofilaxis', 'tromboFarmaco', 'tromboMecanicaTipo', 'anticoagulado', 'anticoagulanteFarmaco', 'sangradoSitio'],
};
const _esRetirable = (sis, k) => (CAMPOS_RETIRABLES[sis] || []).includes(k);

// Pares que NO son ambiguos porque el catálogo tiene su suma. "Clexane, medias
// antitrombóticas" (con coma, que es como se enumera) no es una contradicción:
// el paciente tiene las dos. Se declara el par, no se infiere.
const COMBINABLES = [
  { sistema: 'hemato', k: 'tromboprofilaxis', partes: ['Farmacológica', 'Mecánica'], suma: 'Farmacológica + mecánica' },
];
const _combinar = (sis, k, vivos) => COMBINABLES.find(
  (c) => c.sistema === sis && c.k === k
    && c.partes.length === vivos.length && c.partes.every((p) => vivos.includes(p)),
)?.suma || null;

// ¿Lo que se dijo ANTES bloquea la cue? Negada, retirada o solo planeada.
// Devuelve el motivo (para poder decirlo en el preview) o null.
// `propia` es la cue que YA ES la negativa del catálogo ("sin aislamiento", "no
// ha hecho fiebre"): ahí el "sin" no niega el hallazgo, lo NOMBRA. OJO: `propia`
// exime SOLO de la negación. Eximirla también de PLAN y RETIRO dejaba pasar
// "quedó pendiente saber si está sin aislamiento" como un hecho documentado
// (2ª auditoría), justo la invariante fechada que este archivo cita: un plan no
// es un hecho.
//
// Todas miran hacia ATRÁS y nada más. Las versiones "hacia adelante" que hubo
// entre medio (RETIRO_DESPUES/PLAN_DESPUES) se retiraron en la 3ª auditoría: se
// comían frases enteras y reales ("con sangrado activo y drenaje retirado"
// devolvía null), y su ventana fija de 24 caracteres hacía que el resultado
// dependiera del largo del nombre del dispositivo. Lo que buscaban atajar cae
// hoy en la ruta de colisión.
// El ALCANCE de cada guarda no es el mismo, y la diferencia es de castellano:
// · una NEGACIÓN llega hasta que la frase coordina otra cosa (ver COORDINA).
// · un verbo de RETIRO o de PLAN gobierna todo lo que enumera hasta que la
//   frase vuelve a predicar ("se suspendió el clexane y la heparina" son dos
//   cosas suspendidas), y ese "vuelve a predicar" ya es el corte de cláusula.
function _motivoBloqueo(ctx, idx, texto, propia, veto, retirable) {
  // Todo lo que se diga DESPUÉS de una anticipación, en su misma cláusula, es
  // hipotético. Antes bloqueaba el resto del dictado completo, así que un
  // "avisar cualquier cosa" al principio mataba lo que venía en otra frase.
  if (idx > (ctx.anticipa.get(_clausulaDe(ctx, idx)) ?? Infinity)) return 'plan';
  const antes = _antes(ctx, idx);
  const neg = _negacion(antes, propia);
  if (neg) return neg;
  if (RETIRO.test(antes)) return 'retirado';
  if (PLAN.test(antes)) return 'plan';
  const despues = _despues(ctx, idx, texto.length);
  if (retirable && RETIRO_PARTICIPIO.test(despues)) return 'retirado';
  if (PLAN_POSTERIOR.test(despues)) return 'plan';
  if (PROGRAMADO.test(antes) || PROGRAMADO.test(despues)) return 'programado';
  // El veto propio de la cue mira alrededor: "el CVC sellado con heparina" y
  // "heparina en dosis terapéutica" dicen lo mismo con el calificador a cada
  // lado.
  if (veto && veto.test(`${_ambitoNegacion(antes)} ${texto} ${despues}`)) return 'contexto';
  return null;
}

// TODAS las apariciones no bloqueadas de una cue dentro del tramo, en tramos
// [inicio, fin) de posición ABSOLUTA. Se siguen buscando tras una bloqueada:
// "se suspendió el clexane y quedó con medias" no puede perder las medias.
function _apariciones(ctx, span, ini, re, veto, retirable) {
  const g = new RegExp(re.source, 'g');
  const hits = [];
  const ambiguos = [];
  let bloqueo = null;
  let m;
  while ((m = g.exec(span)) !== null) {
    const abs = ini + m.index;
    const motivo = _motivoBloqueo(ctx, abs, m[0], /^(sin|no)\b/.test(m[0]), veto, retirable);
    // La negación coordinada no descarta: deja la aparición en duda, para que la
    // resolución decida si el campo entero queda en blanco (ver `_resolver`).
    if (motivo === 'negado-coord') ambiguos.push([abs, abs + m[0].length]);
    else if (motivo) bloqueo = bloqueo || motivo;
    else hits.push([abs, abs + m[0].length]);
    if (m.index === g.lastIndex) g.lastIndex += 1;
  }
  return { hits, ambiguos, bloqueo };
}

const _solapa = (a, b) => a[0] < b[1] && b[0] < a[1];

// El motivo que se le MUESTRA a la enfermera. La duda por coordinación tiene el
// suyo propio a propósito: decirle "iba en negativo" sería afirmar justo lo que
// el parser dice no saber, y con "sin dolor y orina clara" el mensaje sería
// derechamente falso. Lo que corresponde es reconocer la duda.
const _motivoAviso = (motivo) => (motivo === 'negado-coord' ? 'dudoso' : motivo);

// DOS COSAS DISTINTAS DEL MISMO CAMPO NO SE RESUELVEN SOLAS. Nunca, y da igual
// dónde caigan en la frase.
//
// La versión anterior resolvía "gana la última cláusula", y la frontera de
// cláusula la pone la coma. El transcriptor no puntúa (Web Speech en es-CL no
// pone comas y el reconocedor nativo tampoco; solo Scribe las entrega, y con
// fallback declarado al navegador), así que la pieza se apoyaba en la rama
// minoritaria: al cambiar la coma por "y", seis de los siete casos de esta
// suite se invertían, y EN SILENCIO. Esto no es un criterio nuevo, es hacer
// cumplir el que está escrito arriba de este archivo: ante la duda, no se
// escribe el campo. Un campo en blanco lo llena la enfermera en dos toques.
// Mismo criterio que `anclarCuracion` en pielLpp.js (ancla solo con
// EXACTAMENTE una lesión candidata) y que el tope de narración larga de
// `parseAccionDictada`: ambigüedad, silencio.
//
// Lo que SÍ se resuelve solo: cuando dos cues del mismo campo matchean el MISMO
// texto ("sin deposiciones" activa a la vez la negativa y la genérica), eso no
// son dos hechos sino dos regex que se solapan, y ahí manda el orden del
// catálogo (lo específico y las negativas primero). Por eso la comparación es
// por SOLAPE de posiciones, no por cláusula: no depende de la puntuación.
// Devuelve `vivos` (los que se dijeron sin duda) y `dudosos` (los que quedaron
// bajo una negación coordinada). Un dudoso jamás se escribe; solo decide si el
// campo queda en blanco por ambigüedad o si se informa como descartado.
function _resolver(porLabel) {
  const ordenadas = Object.entries(porLabel).sort((a, b) => a[1].orden - b[1].orden);
  const reclamados = [];
  const vivos = [];
  const dudosos = [];
  for (const [label, { hits }] of ordenadas) {
    const propios = hits.filter((h) => !reclamados.some((r) => _solapa(r, h)));
    if (!propios.length) continue;
    reclamados.push(...propios);
    vivos.push(label);
  }
  for (const [label, { ambiguos }] of ordenadas) {
    if (vivos.includes(label) || !ambiguos?.length) continue;
    // Un ambiguo que se solapa con lo ya reclamado es ruido de regex, no una
    // segunda cosa dicha (mismo criterio que arriba).
    if (ambiguos.every((a) => reclamados.some((r) => _solapa(r, a)))) continue;
    dudosos.push(label);
  }
  return { vivos, dudosos };
}

// "Balance de 24 horas 450" y "hgt de 12 horas 145": el primer número es la
// DURACIÓN del control, no el valor. Es el espejo del marcador de hora en el
// hueco (CORTE_NUM) pero por el otro lado, y sin esto el 24 se escribía como
// balance y el 450 se perdía mudo. Cuando el número capturado viene seguido de
// "hora(s)", se salta y se busca el valor real ahí mismo; el hueco que cuenta
// para CORTE_NUM pasa a ser el segundo, porque el "de las" del principio se
// refería a la duración.
const RE_DURACION = new RegExp(`^\\s*horas?\\b([^\\d,;:.\\n]{0,24})${NUM}`);
function _saltarDuracion(span, m) {
  const resto = span.slice(m.index + m[0].length);
  const cont = resto.match(RE_DURACION);
  if (!cont) {
    // "hgt cada 12 horas" / "balance de 12 horas y diuresis por foley": el
    // número seguido de "horas" SIN un valor detrás es una duración a secas, no
    // el valor (residual de la 8ª auditoría: con 6 u 8 el rango lo atajaba,
    // con 12 o 24 se firmaba una hipoglicemia que nadie tuvo). Se descarta con
    // aviso; el fail-safe es no escribir, jamás escribir el reloj.
    if (/^\s*horas?\b/.test(resto)) return { valor: m[2], hueco: m[1], largo: m[0].length, soloDuracion: true };
    return { valor: m[2], hueco: m[1], largo: m[0].length };
  }
  return { valor: cont[2], hueco: cont[1], largo: m[0].length + cont[0].length, duracion: m[2] };
}

// Los números de una palabra clave dentro del tramo, con la MISMA regla: dos
// valores distintos para el mismo campo no se resuelven solos ("pcr hoy 12, la
// pcr de ayer era 300" firmaba el 300 como el de ahora). Lo descartado se
// devuelve para que el preview lo diga: un número dicho que no aterriza y nadie
// avisa es la peor forma de perder un dato.
function _numeros(ctx, span, ini, re, k) {
  const g = new RegExp(re.source, 'g');
  const valores = [];
  const descartes = [];
  let m;
  while ((m = g.exec(span)) !== null) {
    const abs = ini + m.index;
    if (m.index === g.lastIndex) g.lastIndex += 1;
    // Un número bloqueado por una guarda también se avisa: hasta la 4ª auditoría
    // se descartaba en silencio y el preview no decía nada.
    // "hgt de 12 horas 145": el primer número es la duración, no el valor.
    const { valor, hueco, largo, soloDuracion } = _saltarDuracion(span, m);
    g.lastIndex = Math.max(g.lastIndex, m.index + largo);
    if (soloDuracion) { descartes.push({ valor, motivo: 'sujeto' }); continue; }
    const bloqueo = _motivoBloqueo(ctx, abs, m[0], false);
    if (bloqueo) { descartes.push({ valor, motivo: _motivoAviso(bloqueo) }); continue; }
    // El número solo es de ESTA palabra clave si el hueco no cambió de sujeto.
    if (CORTE_NUM.test(hueco)) { descartes.push({ valor, motivo: 'sujeto' }); continue; }
    const n = _aNumero(valor);
    if (n === null) continue;
    if (!_enRango(k, n)) { descartes.push({ valor, motivo: 'rango' }); continue; }
    if (!valores.includes(String(n))) valores.push(String(n));
  }
  return { valores, descartes };
}

// Los multi devuelven además lo que una guarda les descartó: un cultivo que no
// aterriza tiene que decirse igual que un número que no aterriza (hasta la 4ª
// auditoría se perdía mudo).
function _matchMulti(ctx, span, ini, cues) {
  const out = [];
  let bloqueo = null;
  for (const [label, re, veto] of cues) {
    const r = _apariciones(ctx, span, ini, re, veto);
    // En un multi la duda por negación coordinada se trata como bloqueo: no se
    // suma el label y se avisa. No hay nada que "elegir" en una lista.
    if (r.hits.length) { if (!out.includes(label)) out.push(label); } else if (r.bloqueo || r.ambiguos.length) bloqueo = bloqueo || _motivoAviso(r.bloqueo) || 'dudoso';
  }
  return { labels: out, bloqueo };
}

// Dos palabras que solo cuentan si van PEGADAS (misma cláusula, hueco corto):
// el ancla obligatoria de las cues que sueltas colisionan con otro sistema.
const cerca = (a, b, hueco = 16) => new RegExp(`${a}[^.;,]{0,${hueco}}${b}|${b}[^.;,]{0,${hueco}}${a}`);

// ── Renal ─────────────────────────────────────────────────────────────────────
// El aspecto de la orina EXIGE su ancla pegada. Sin ella, "concentrado de
// glóbulos rojos" escribía orina "Concentrada" y una "gasa clara" escribía
// orina "Clara" (bugs reales): ninguna cue de una palabra suelta sin ancla.
const ORINA = (aspecto) => new RegExp(`(orina|orino|diuresis)[^.;,]{0,12}(${aspecto})`);

const RENAL_SELECTS = {
  diuresisVia: [
    ['Catéter urinario permanente', /\bfoley\b|cateter urinario|sonda vesical|sonda urinaria|\bcup\b/],
    ['Sondeo intermitente', /sondeo intermitente|sondeo cada/],
    ['Urostomía', /urostom/],
    ['Espontánea (chata, pato o pañal)', /diuresis espontanea|orina espontanea|\bchata\b|\bpato\b|\bpanal\b/],
  ],
  orina: [
    ['Coluria', /coluri/],
    ['Hematúrica', /hematuri|orina con sangre|orina hematica/],
    ['Piúrica', /piuri|orina turbia/],
    ['Concentrada', ORINA('concentrad\\w*|oscura')],
    ['Clara', ORINA('clara|diluida')],
    ['Amarilla', ORINA('amarill\\w*')],
  ],
  diuresisEstado: [
    ['Anuria', /anuri|\bsin diuresis|\bno esta orinando/],
    ['Oliguria', /oligur|poca diuresis|diuresis baja|escasa diuresis/],
    ['Poliuria', /poliuri/],
    ['Diuresis adecuada', /diuresis adecuada|buena diuresis|diuresis conservada/],
  ],
};

// El balance se dice con signo hablado ("balance negativo 500"), no con menos.
// "negativo" y "menos" dicen el signo, no niegan el dato: por eso el hueco se
// revisa con CORTE_NUM (otro analito de por medio) y las guardas corren sobre lo
// que se dijo alrededor de la palabra balance, no sobre el hueco.
function _balance(ctx, span, ini) {
  const g = new RegExp(`\\bbalance\\b([^\\d,;:.\\n]{0,24})${NUM}`, 'g');
  const valores = [];
  const descartes = [];
  let m;
  while ((m = g.exec(span)) !== null) {
    const abs = ini + m.index;
    if (m.index === g.lastIndex) g.lastIndex += 1;
    // Los MISMOS avisos que `_numeros`: hasta la 6ª auditoría el balance era el
    // único número que se caía mudo cuando una guarda lo bloqueaba.
    const { valor, hueco, largo, soloDuracion } = _saltarDuracion(span, m);
    g.lastIndex = Math.max(g.lastIndex, m.index + largo);
    if (soloDuracion) { descartes.push({ valor, motivo: 'sujeto' }); continue; }
    const bloqueo = _motivoBloqueo(ctx, abs, m[0], false);
    if (bloqueo) { descartes.push({ valor, motivo: _motivoAviso(bloqueo) }); continue; }
    if (CORTE_NUM.test(hueco)) { descartes.push({ valor, motivo: 'sujeto' }); continue; }
    const n = _aNumero(valor);
    if (n === null) continue;
    const conSigno = /negativ|menos/.test(`${m[1]} ${hueco}`) ? -Math.abs(n) : n;
    if (!_enRango('balance', conSigno)) { descartes.push({ valor, motivo: 'rango' }); continue; }
    if (!valores.includes(String(conSigno))) valores.push(String(conSigno));
  }
  return { valores, descartes };
}

// ── Gastro ────────────────────────────────────────────────────────────────────
const GASTRO_SELECTS = {
  regimen: [
    ['Régimen cero', /regimen cero|regimen 0|nada por boca|\bnpo\b/],
    ['Nutrición enteral', /nutricion enteral|alimentacion enteral|regimen enteral/],
    // "Parenteral" a secas era ambigua: la vía parenteral de un fármaco no es
    // el régimen alimentario del paciente.
    ['Nutrición parenteral', /nutricion parenteral|alimentacion parenteral|regimen parenteral|\bnpt\b/],
    // "Vía oral" a secas era la vía de un FÁRMACO leída como régimen (caso G,
    // 2026-09-07: "cloruro de potasio en cápsulas vía oral" escribía
    // "Vía oral." en gastro). El régimen exige contexto de alimentación.
    ['Vía oral', /alimentacion (via )?oral|regimen (oral|hidrico|liquido|liviano|blando|comun|papilla)|(regimen|dieta|alimentacion|se alimenta|come|tolera|tolerando|ingesta|realimentacion)[^.;]{0,30}\bvia oral\b|\bvia oral\b[^.;]{0,30}(tolera|alimentacion|regimen|dieta|ingesta)/],
  ],
  regimenOral: [
    ['Hídrico', /hidrico/], ['Líquido', /regimen liquido/], ['Liviano', /liviano/], ['Blando', /blando/],
    ['Común', /regimen comun/], ['Papilla', /papilla/],
  ],
  // Ingesta (2026-09-29): solo las formas que la nombran, para no leer "come"
  // suelto como un dato de cuánto comió.
  ingesta: [
    ['Buena ingesta', /buena ingesta|ingesta (buena|adecuada|completa)/],
    ['Ingesta regular', /ingesta (regular|parcial)/],
    ['Mala ingesta', /mala ingesta|ingesta (mala|escasa|pobre|insuficiente|deficiente)/],
    ['Rechazo alimentario', /rechazo alimentario|rechaza (la )?(alimentacion|comida|regimen)/],
  ],
  tolerancia: [
    ['Mal tolerado', /mal tolerad|\bno tolera|intoleran/],
    ['Bien tolerado', /bien tolerad|tolera bien|buena tolerancia|tolerando bien/],
  ],
  deposiciones: [
    ['Ausentes', /\bsin deposicion|\bsin transito|\bno ha (obrado|defecado)|\bno (hubo|hay|ha habido|presenta|ha presentado) deposicion|\bno ha hecho deposicion|ausencia de deposicion|\bdeposicion(?:es)?\s*:?\s*(?:\(\s*[-−]\s*\)|(?:ausentes?|negativas?)\b)/],
    // La mera mención no constata tránsito. Los signos pertenecen a la
    // deposición nombrada, no a otro dato de la nota («deposiciones (-)»).
    ['Presentes', /\bdeposicion(?:es)?\s*:?\s*(?:\(\s*\+\s*\)|(?:presentes?|positivas?)\b)|\b(?:con|hubo|hay|presenta|ha presentado|hizo) deposicion|\bobro\b|\bdefeco\b|\bdiarrea\b/],
  ],
};
const GASTRO_MULTIS = {
  toleranciaSintomas: [
    ['Náuseas', /nausea/], ['Vómitos', /vomit/], ['Distensión', /distension|distendid/],
    ['Dolor abdominal', /dolor abdominal/], ['Residuo elevado', /residuo (elevado|alto)/],
    ['Diarrea', /diarrea/],
  ],
};

// "hace dos días" se dice con la palabra, no con el dígito.
const PALABRA_NUMERO = { un: 1, uno: 1, una: 1, dos: 2, tres: 3, cuatro: 4, cinco: 5, seis: 6, siete: 7, ocho: 8, nueve: 9, diez: 10 };
function _diasSinTransito(span) {
  const m = span.match(/(?:hace|lleva|van)\s+([a-z]+|\d+)\s+dias?/)
    || span.match(/(\d+)\s+dias?\s+sin\s+(transito|deposicion)/);
  if (!m) return null;
  const bruto = m[1];
  const n = /^\d+$/.test(bruto) ? Number(bruto) : PALABRA_NUMERO[bruto];
  return Number.isFinite(n) && n > 0 && _enRango('depositosDias', n) ? n : null;
}

// ── Infecto ───────────────────────────────────────────────────────────────────
const INFECTO_SELECTS = {
  estadoFebril: [
    ['Afebril', /\bafebril\b|\bsin fiebre|\bno ha hecho fiebre|\bno hizo fiebre/],
    ['Febrícula', /febricula|subfebril/],
    ['Fiebre persistente', /fiebre persistente|fiebre mantenida|sigue febril|fiebre que no cede/],
    ['Peaks febriles', /peaks? febril|hizo fiebre|alza termica|pico febril|febril hasta/],
  ],
  aislamiento: [
    // "Sin aislamiento" solo cuando NO viene el tipo detrás: "sin aislamiento de
    // contacto" es ambiguo igual que "no hay aislamiento de contacto" (puede ser
    // que no haya ninguno, o que el que hay no sea de contacto), y las dos formas
    // tienen que resolverse igual: campo en blanco y aviso. Antes una caía en
    // silencio y la otra escribía, por un accidente de solape.
    ['Sin aislamiento', /\bsin aislamiento(?! (de |por )?(contacto|gotit|aereo|protector))/],
    ['Aislamiento de contacto', /aislamiento (de |por )?contacto/],
    ['Aislamiento de gotitas', /aislamiento (de |por )?gotit/],
    ['Aislamiento aéreo', /aislamiento aereo/],
    ['Aislamiento protector', /aislamiento protector/],
  ],
  // El adjetivo "inflamatorios" es obligatorio: son los parámetros de ESTE
  // sistema, no los hemodinámicos ni los ventilatorios.
  parametros: [
    ['En ascenso', /parametros inflamatorios[^.;]{0,24}((en )?ascenso|al alza)/],
    ['En descenso', /parametros inflamatorios[^.;]{0,24}((en )?descenso|bajando)/],
    ['Estables', /parametros inflamatorios[^.;]{0,24}estables/],
  ],
};
const INFECTO_MULTIS = {
  cultivoSitio: [
    ['Hemocultivo', /hemocultivo/],
    ['Urocultivo', /urocultivo/],
    ['Cultivo de secreción respiratoria', /cultivo (de )?secrecion respiratoria|cultivo traqueal|aspirado traqueal/],
    // "arrastre" suelto es una sábana de arrastre, no un cultivo (bug real).
    ['Cultivo de arrastre', /cultivos? (de |por )?arrastre|arrastre (nasal|rectal|inguinal|axilar|de vigilancia)/],
    ['Punta de catéter', /punta de cateter/],
    ['Cultivo de herida', /cultivo de herida/],
    ['LCR', /\blcr\b|liquido cefalorraquideo/],
  ],
};

// ── Hemato ────────────────────────────────────────────────────────────────────
// La heparina del sello de un catéter o de una infusión terapéutica no es
// tromboprofilaxis: mismo criterio (y mismo porqué) que el veto del circuito de
// diálisis, pero mirando solo el contexto pegado a la palabra, para no perder un
// clexane dicho antes en la misma frase.
const VETO_HEPARINA = /sell\w*|permeabiliz\w*|lavad\w*|purga|\bbic\b|infusion continua|dosis terapeutica|terapeutic\w*|anticoagul\w*/;

// LA FINALIDAD NO SE DEDUCE DEL FÁRMACO (error prioritario 2026-09-07: una
// paciente ANTICOAGULADA con enoxaparina salía en el documento con
// "tromboprofilaxis farmacológica con HBPM"; la etiqueta nacía AQUÍ, en la
// captura, no en la IA ni en el pulido). Enoxaparina, HBPM, heparina o
// fondaparinux solos no escriben ninguna finalidad: la tromboprofilaxis exige
// que el relato la nombre (profilaxis, profiláctica, tromboprofilaxis) y la
// anticoagulación terapéutica exige que la diga (anticoagulada, TACO, dosis
// terapéutica o plena). Sin finalidad dicha, el fármaco viaja en la nota tal
// cual y el documento lo nombra sin asignarle un para qué.
// "profilaxis" a secas cuenta como finalidad (con el fármaco en la misma
// cláusula), salvo la gástrica o antibiótica, que son otra cosa.
const PROFILAXIS_RE = 'tromboprofilaxis|\\bprofilaxis\\b(?! gastric| de ulcera| antibiotic| antimicrob| de estres| con (omeprazol|famotidina|ranitidina))|dosis profilactica|profilactic\\w*';
const ANTITROMBOTICO_RE = 'clexane|enoxaparina|\\bhbpm\\b|\\bhnf\\b|heparina|fondaparinux';
const conProfilaxis = (farmacoRe) => new RegExp(`(${PROFILAXIS_RE})[^.;]{0,40}(${farmacoRe})|(${farmacoRe})[^.;]{0,40}(${PROFILAXIS_RE})`);

// Dos finalidades para el MISMO anticoagulante son incompatibles: profilaxis
// farmacológica y anticoagulación terapéutica con fármacos de la misma familia
// (o sin fármaco declarado en alguna de las dos). Con fármacos distintos
// pueden coexistir (HNF profiláctica más warfarina). Fuente única: la usan la
// corrección por dictado (mergeDictadoEnEvolucion) y el aviso de la Mesa.
const MISMA_FAMILIA = { 'HBPM (enoxaparina)': 'HBPM dosis terapéutica', 'HNF': 'HNF en BIC' };
export function finalidadesIncompatibles(campos) {
  const tp = campos?.tromboprofilaxis;
  if (tp !== 'Farmacológica' && tp !== 'Farmacológica + mecánica') return false;
  if (campos?.anticoagulado !== 'Con anticoagulación terapéutica') return false;
  const fp = campos?.tromboFarmaco;
  const fa = campos?.anticoagulanteFarmaco;
  return !fp || !fa || MISMA_FAMILIA[fp] === fa;
}

const HEMATO_SELECTS = {
  tromboprofilaxis: [
    ['Sin tromboprofilaxis', /\bsin tromboprofilaxis|\bsin profilaxis antitrombotica|\bno (es|era|esta en|tiene|recibe|lleva) (trombo)?profilaxis/],
    // Un fármaco ENUMERADO con una medida mecánica ("clexane y medias") NO
    // expresa la finalidad del fármaco (revisión del 2026-09-07): la medida
    // mecánica es profilaxis por sí misma, el fármaco no hereda ese para qué.
    // Solo "farmacológica y mecánica" dicho, o la profilaxis nombrada junto a
    // la medida mecánica.
    ['Farmacológica + mecánica', new RegExp(`(tromboprofilaxis |profilaxis )?farmacologica (y|\\+|mas) mecanica|(${PROFILAXIS_RE})[^.;]{0,40}(y|mas)[^.;]{0,12}(medias|compresion neumatica|botas)`), VETO_HEPARINA],
    ['Mecánica', /solo medias|medias antitromb|compresion neumatica|botas neumaticas|tromboprofilaxis (mecanica|no farmacologica)|\bcni\b/],
    ['Farmacológica', new RegExp(`tromboprofilaxis farmacologica|${conProfilaxis(ANTITROMBOTICO_RE).source}`), VETO_HEPARINA],
  ],
  tromboFarmaco: [
    ['HBPM (enoxaparina)', conProfilaxis('clexane|enoxaparina|\\bhbpm\\b'), VETO_HEPARINA],
    ['HNF', conProfilaxis('\\bhnf\\b|heparina (no fraccionada|sodica)'), VETO_HEPARINA],
    ['Fondaparinux', conProfilaxis('fondaparinux')],
  ],
  // Anticoagulación TERAPÉUTICA: el otro para qué del mismo fármaco. Solo con
  // la palabra dicha; el fármaco cuelga de la rama (dependeDe), así que un
  // "enoxaparina 60 mg" sin finalidad no escribe ninguno de los dos campos.
  anticoagulado: [
    ['Sin anticoagulación terapéutica', /\bsin anticoagula\w*|\bno esta anticoagulad\w*/],
    ['Con anticoagulación terapéutica', /anticoagulad[oa]s?\b|anticoagulacion( terapeutica| plena)?\b|\btaco\b|dosis (terapeutica|plena|de anticoagulacion)|rango terapeutico/],
  ],
  anticoagulanteFarmaco: [
    ['HNF en BIC', /(\bhnf\b|heparina)[^.;]{0,20}\bbic\b|\bbic\b[^.;]{0,20}(\bhnf\b|heparina)|heparina en infusion/],
    ['HBPM dosis terapéutica', /clexane|enoxaparina|\bhbpm\b/],
    ['Warfarina', /warfarina|coumadin/],
    ['NACO', /\bnaco\b|rivaroxaban|apixaban|dabigatran|edoxaban|xarelto|eliquis/],
  ],
  tromboMecanicaTipo: [
    ['Compresión neumática intermitente', /(?:tromboprofilaxis no farmacologica con )?(?:compresion neumatica|botas neumaticas|\bcni\b)/],
    ['Medias antitrombóticas', /\bmedias\b/],
  ],
  transfusion: [
    ['No', /\bsin transfusion|\bno se transfund|\bno requirio transfusion/],
    // Solo el pasado: "se transfundirá" es un plan, y el plan no es un hecho.
    ['Sí', /transfundi(o|eron|mos|da|do)\b|se transfunde\b|transfusion de\b/],
  ],
  sangrado: [
    // "no tiene sangrado activo" es la NEGATIVA del catálogo, no el hallazgo:
    // se nombra entera para que caiga en la opción correcta en vez de quedar en
    // blanco (la guarda de negación sola la dejaba sin escribir).
    ['Sin signos de sangrado', /\bsin (signos de )?sangrado|\bno sangra|\bsin sangramiento|\bno (tiene|hay|presenta|se ve|se observa)[^.;,]{0,12}sangr/],
    // "sangró por la herida" en pasado es la forma normal de contarlo, y sin
    // ella la frase encadenada se resolvía sola a la negativa (3ª auditoría).
    // OJO: "sangrado por" NO entra acá a propósito ("sin signos de sangrado por
    // la herida" es la negativa, no el hallazgo).
    ['Con sangrado activo', /sangrado activo|esta sangrando|sangr[ao] por|sangraba|sangramiento activo/],
  ],
  sangradoSitio: [
    ['Digestivo', new RegExp(`melena|hematemesis|${cerca('sangr\\w*', 'digestiv').source}`)],
    ['Herida quirúrgica', cerca('sangr\\w*', 'herida (quirurgica|operatoria)')],
    // "drenaje" suelto es un drenaje cualquiera, no el sitio del sangrado.
    ['Por drenaje', cerca('sangr\\w*', 'drenaje')],
    ['Sitio de punción', /sitio de puncion/],
    ['Epistaxis', /epistaxis/],
  ],
  // La rama del laboratorio: sin ella, un valor suelto no tiene dónde caer
  // (dependeDe). "Llegaron los exámenes" es exactamente lo que se dice cuando
  // por fin se sabe.
  labHemato: [
    ['Sin laboratorio nuevo', /\bsin laboratorio nuevo|\bsin examenes nuevos/],
    ['Con laboratorio del día', /laboratorio del dia|laboratorio de hoy|examenes de (sangre|hoy|la manana)|llegaron los examenes|control de laboratorio/],
  ],
};
const HEMATO_MULTIS = {
  // OJO: "plaquetas 90" es el VALOR de laboratorio, no una transfusión de
  // plaquetas — por eso el hemocomponente exige decirlo entero.
  transfusionQue: [
    ['Glóbulos rojos', /globulos rojos|hematies/],
    ['Plaquetas', /(unidad(es)? de|transfusion de|transfundi(eron)?) plaquetas/],
    ['Plasma fresco', /plasma (fresco|congelado)|\bpfc\b|(unidad(es)?|transfusion) de plasma/],
    ['Crioprecipitado', /crioprecipitad/],
  ],
};

// Números por sistema: solo los que se dicen SIN ambigüedad de magnitud (ver
// FUERA_DE_ALCANCE para los que quedaron afuera justamente por eso). Cada
// regex captura (hueco)(número) para que el hueco se pueda revisar con
// CORTE_NUM, y el valor pasa además por su rango plausible.
// ── Ventilación / Neuro / Hemodinamia: SOLO selects inequívocos ──────────────
// Alcance deliberado de la primera rebanada (anotado en el plan): los números
// compartidos entre ramas (FiO2 vive en cuatro campos según el soporte) NO se
// mapean todavía — asignarlos sin su contexto sería adivinar la rama, y la
// doctrina es silencio con aviso, no ruleta. El PEEP sí entra: solo existe en
// VMI (el de la VNI se llama EPAP en el catálogo y se dicta como EPAP).
const VENTILACION_SELECTS = {
  viaAerea: [
    ['Vía aérea nativa', /via aerea nativa/],
    ['Vía aérea artificial', /via aerea artificial/],
  ],
  soporte: [
    // "sin requerir oxigenoterapia" del dictado real ES aire ambiente: la
    // negación viene DENTRO de la cue (patrón de 'Afebril').
    ['Aire ambiente', /aire ambiente|aire ambiental|sin (requerir )?(oxigeno(terapia)?|o2)\b/],
    ['CNAF', /\bcnaf\b|alto flujo/],
    ['VNI', /\bvni\b|bipap|\bcpap\b/],
    ['Oxigenoterapia', /oxigenoterapia|naricera|mascarilla (simple|venturi|de (alto flujo|reservorio|recirculacion))/],
  ],
  viaArtificial: [
    ['TOT', /tubo orotraqueal|\btot\b|\btet\b/],
    ['TQT', /\btqt\b|traqueostom/],
  ],
  // Cuelga de viaArtificial TQT (el filtro de dependeDe lo deja fuera para un
  // TOT que también se dice "conectado a VMI"): es la vía del catálogo para
  // que vmiModo aplique con traqueostomía.
  tqtSoporte: [
    ['En VMI', /conectad[oa] a (la )?vmi|en vmi\b|a ventilacion mecanica/],
    ['Con filtro humidificador', /filtro humidificador|nariz artificial/],
    ['En plan de decanulación', /decanulacion/],
  ],
  vmiModo: [
    ['Volumen control', /volumen control(ado)?|modo vc\b/],
    ['Presión control', /presion control(ada)?|modo pc\b/],
    ['Espontáneo', /modo espontaneo|en espontaneo|presion de soporte/],
  ],
};
// ── Suspensión de un fármaco de sedoanalgesia (2026-09-08, caso L) ─────────
// Tres datos que NO se confunden: el estado de CADA fármaco (la lista
// `sedoanalgesia` + `sedoDetalle`), el estado GLOBAL de sedación (`sedacion`)
// y la valoración (RASS, que no tiene cue: viaja en la nota). "Se suspende
// propofol" cierra ESE fármaco y nada más: no se traduce a "sin sedación"
// (queda sin aclarar si era el único, y el merge lo dice) ni se deduce del
// RASS. Solo verbos de SUSPENSIÓN: "se baja/disminuye propofol" es titulación,
// no retiro, y RETIRO (más abajo) los incluye a propósito para otras guardas.
// Cues 1:1 con las opciones del catálogo de `sedoanalgesia`; el nombre del
// fármaco sin verbo de suspensión no escribe nada (la nota lo lleva).
const SEDANTES_CUES = [
  ['Propofol', /\bpropofol\b/],
  ['Midazolam', /\bmidazolam\b/],
  ['Dexmedetomidina', /\bdexmedetomidina\b|\bprecedex\b/],
  ['Remifentanilo', /\bremifentanil\w*/],
  ['Fentanilo', /\bfentanil\w*/],
  ['Ketamina', /\bketamina\b/],
  ['Esketamina', /\besketamina\b/],
  ['Morfina', /\bmorfina\b/],
];
const SUSPENSION = /\b(suspend\w*|suspensi\w*|retir\w*|quit\w*|discontinu\w*|descontinu\w*|saco|sacaron|sacamos)\b/;
const SUSPENSION_PARTICIPIO = /^\s*(que (ya |)(fue|habia sido|esta|quedo) |ya |fue |esta |quedo )?(suspendid|retirad|sacad|discontinuad|descontinuad)\w*/;

const NEURO_SELECTS = {
  sedacion: [
    ['Con sedación', /con sedacion|sedad[oa]\b|bajo seda|sedoanalgesia/],
    ['Sin sedación', /sin sedacion/],
  ],
  // "Sopor" a secas NO se mapea: superficial y profundo son opciones distintas
  // y elegir por la usuaria sería adivinar (la pista igual abre el tramo, así
  // que el dictado cae con aviso de que no calzó, no en silencio).
  conciencia: [
    ['Vigil', /\bvigil\b|despiert[oa]\b/],
    ['Somnoliento', /somnolient/],
    ['Sopor superficial', /sopor superficial/],
    ['Sopor profundo', /sopor profundo/],
    ['Coma', /\ben coma\b/],
  ],
};
const HEMODINAMIA_SELECTS = {
  presion: [
    ['Hipotenso', /hipotens/],
    ['Hipertenso', /hipertens/],
    ['Normotenso', /normotens/],
  ],
  fc: [
    ['Bradicárdico', /bradicard/],
    ['Taquicárdico', /taquicard/],
    ['Normocárdico', /normocard/],
  ],
  ritmo: [
    ['Sinusal', /ritmo sinusal/],
    ['Fibrilación auricular', /fibrilacion auricular|\bacfa\b/],
  ],
  dvaSoporte: [
    ['Sin drogas vasoactivas', /sin (drogas? vasoactivas?|\bdva\b|apoyo vasoactivo|noradrenalina)/],
    ['Con drogas vasoactivas', /con (drogas? vasoactivas?|\bdva\b)|noradrenalina|\bnad\b/],
  ],
  perfusion: [
    ['Bien perfundido', /bien perfundid/],
    ['Hipoperfusión periférica', /hipoperfusion|mal perfundid/],
  ],
};

const NUMS = {
  gastro: { hgt: reNum('(?:\\bhgt\\b|glicemia|hemoglucotest)') },
  infecto: {
    pcr: reNum('\\bpcr\\b'),
    procalcitonina: reNum('procalcitonin\\w*'),
    lactico: reNum('(?:\\blactic\\w*|\\blactato)'),
    // El grupo vacío es el "hueco" que el resto de las cues numéricas captura:
    // acá el número va pegado a la palabra, pero la forma se mantiene para que
    // la cosecha lea siempre los mismos grupos.
    atbDia: /\bdia( )(\d{1,2}) de (tratamiento|antibiotico|atb)/,
  },
  hemato: { hb: reNum('(?:hemoglobina|\\bhb\\b)') },
  // El PEEP solo existe en VMI: sin ambigüedad de rama (ver el comentario de
  // VENTILACION_SELECTS para el porqué del alcance).
  ventilacion: { vmiPeep: reNum('\\bpeep\\b') },
};

const SELECTS = {
  renal: RENAL_SELECTS, gastro: GASTRO_SELECTS, infecto: INFECTO_SELECTS, hemato: HEMATO_SELECTS,
  ventilacion: VENTILACION_SELECTS, neuro: NEURO_SELECTS, hemodinamia: HEMODINAMIA_SELECTS,
};
const MULTIS = {
  renal: {}, gastro: GASTRO_MULTIS, infecto: INFECTO_MULTIS, hemato: HEMATO_MULTIS,
  ventilacion: {}, neuro: {}, hemodinamia: {},
};

export const SISTEMAS_DICTADO = ['renal', 'gastro', 'infecto', 'hemato', 'ventilacion', 'neuro', 'hemodinamia'];

const defDe = (sisId, k) => (CAMPOS_POR_SISTEMA[sisId] || []).find((c) => c.k === k) || null;

// ── El parser ─────────────────────────────────────────────────────────────────
// `evolucion` (opcional) es el estado de la evolución del paciente: se lee SOLO
// para resolver `dependeDe` (un campo tras una rama se llena si su raíz quedó
// marcada en el mismo dictado o YA estaba respondida). Nunca se escribe acá:
// eso es trabajo del merge.
export function parseEvolucionDictada(texto, evolucion) {
  const norm = _norm(texto);
  if (!norm) return null;
  // `anticipa` es dónde empieza lo hipotético en cada cláusula: lo dicho después
  // de esa posición no se escribe (ver ANTICIPATORIO). Lo anterior es hecho
  // constatado.
  const ctx = { norm, cortes: _cortes(norm) };
  ctx.anticipa = _anticipaciones(norm, ctx);

  // 1) Cosecha por tramo. Los select juntan las APARICIONES de cada cue (con su
  //    posición) y se resuelven después; los multi suman; los números juntan
  //    valores distintos. Todo lo que una guarda descarta se anota para poder
  //    decirlo: un campo que se cae sin dejar rastro es la peor forma de
  //    perder un dato, porque ella ve una card con menos líneas y no tiene cómo
  //    saber que faltó algo.
  const bruto = {};
  const cands = {};
  const nums = {};
  const fallidos = [];
  const retirosNeuro = new Set(); // fármacos de sedoanalgesia que este dictado SUSPENDE
  for (const { sis, texto: span, ini } of _spans(norm)) {
    if (VETOS[sis] && VETOS[sis].test(span)) continue;
    const destino = bruto[sis] || (bruto[sis] = {});
    const porCampo = cands[sis] || (cands[sis] = {});
    const porNumero = nums[sis] || (nums[sis] = {});
    for (const [k, cues] of Object.entries(SELECTS[sis] || {})) {
      const acumulado = porCampo[k] || (porCampo[k] = {});
      const retirable = _esRetirable(sis, k);
      cues.forEach(([label, re, veto], orden) => {
        const { hits, ambiguos, bloqueo } = _apariciones(ctx, span, ini, re, veto, retirable);
        if (hits.length || ambiguos.length) {
          const previo = acumulado[label] || (acumulado[label] = { orden, hits: [], ambiguos: [] });
          previo.hits.push(...hits);
          previo.ambiguos.push(...ambiguos);
        }
        if (bloqueo) fallidos.push({ sistema: sis, k, motivo: bloqueo });
      });
    }
    for (const [k, cues] of Object.entries(MULTIS[sis] || {})) {
      const { labels, bloqueo } = _matchMulti(ctx, span, ini, cues);
      if (bloqueo) fallidos.push({ sistema: sis, k, motivo: bloqueo });
      if (!labels.length) continue;
      destino[k] = Array.from(new Set([...(destino[k] || []), ...labels]));
    }
    for (const [k, re] of Object.entries(NUMS[sis] || {})) {
      const { valores, descartes } = _numeros(ctx, span, ini, re, k);
      porNumero[k] = [...new Set([...(porNumero[k] || []), ...valores])];
      descartes.forEach((d) => fallidos.push({ sistema: sis, k, ...d }));
    }
    if (sis === 'renal') {
      const { valores, descartes } = _balance(ctx, span, ini);
      porNumero.balance = [...new Set([...(porNumero.balance || []), ...valores])];
      descartes.forEach((d) => fallidos.push({ sistema: sis, k: 'balance', ...d }));
    }
    if (sis === 'gastro' && destino.depositosDias === undefined) {
      const d = _diasSinTransito(span);
      if (d !== null) destino.depositosDias = String(d);
    }
    if (sis === 'gastro') {
      // Mantiene el aviso de negación/plan ante una mención sin predicado,
      // pero nunca convierte esa mención en una observación positiva.
      const mencion = _apariciones(ctx, span, ini, /\bdeposicion(?:es)?\b/, null, false);
      if (mencion.bloqueo) fallidos.push({ sistema: sis, k: 'deposiciones', motivo: mencion.bloqueo });
      if (mencion.ambiguos.length) fallidos.push({ sistema: sis, k: 'deposiciones', motivo: 'dudoso' });
    }
    if (sis === 'neuro') {
      const vigentes = evolucion?.sistemas?.neuro?.campos?.sedoanalgesia;
      const lista = Array.isArray(vigentes) ? vigentes : [];
      for (const [label, re] of SEDANTES_CUES) {
        const g = new RegExp(re.source, 'g');
        let m;
        while ((m = g.exec(span)) !== null) {
          const abs = ini + m.index;
          const antes = _antes(ctx, abs);
          const despues = _despues(ctx, abs, m[0].length);
          const suspendido = SUSPENSION.test(antes) || SUSPENSION_PARTICIPIO.test(despues);
          if (!suspendido) continue;
          // Las mismas guardas que cualquier cue: negado o anunciado no es
          // hecho. `_motivoBloqueo` responde 'retirado' ANTES de mirar el plan
          // (para las cues normales da igual: ambos bloquean), así que el plan
          // se mira aquí aparte: "se va a suspender" o "suspender propofol a
          // las 14" es lo que falta hacer, no lo hecho.
          const motivo = _motivoBloqueo(ctx, abs, m[0], false, null, false);
          const anunciado = PLAN.test(antes) || /\b(suspender|suspendera|suspenderan|retirar|retirara|quitar)\b/.test(antes);
          if (anunciado) { fallidos.push({ sistema: sis, k: 'sedoanalgesia', motivo: 'plan', valor: label }); continue; }
          if (motivo && motivo !== 'retirado') { fallidos.push({ sistema: sis, k: 'sedoanalgesia', motivo, valor: label }); continue; }
          if (!lista.includes(label)) { fallidos.push({ sistema: sis, k: 'sedoanalgesia', motivo: 'no_registrado', valor: label }); continue; }
          retirosNeuro.add(label);
        }
      }
    }
  }

  // 1b) Resolución. Con UNA sola cosa dicha del campo, se escribe. Con dos, no
  //     se resuelve sola: se reporta y ella la toca (ver `_resolver`).
  const colisiones = [];
  for (const [sis, porCampo] of Object.entries(cands)) {
    const destino = bruto[sis] || (bruto[sis] = {});
    for (const [k, porLabel] of Object.entries(porCampo)) {
      const { vivos, dudosos } = _resolver(porLabel);
      // Un dudoso solo (nadie más dijo nada del campo): no se escribe y se
      // informa como negado, que es lo que la enfermera necesita ver.
      if (!vivos.length) {
        if (dudosos.length) fallidos.push({ sistema: sis, k, motivo: 'dudoso' });
        continue;
      }
      const opciones = [...vivos, ...dudosos];
      if (opciones.length > 1) {
        // Salvo que el catálogo tenga la SUMA de las dos: un paciente con
        // clexane y medias no es un dictado ambiguo, es un paciente con las dos
        // cosas, y "Farmacológica + mecánica" existe justo para eso. Se declara
        // el par en vez de inferirlo (ver COMBINABLES).
        const suma = dudosos.length ? null : _combinar(sis, k, vivos);
        if (suma) { destino[k] = suma; continue; }
        colisiones.push({ sistema: sis, k, opciones });
        continue;
      }
      [destino[k]] = vivos;
    }
  }
  for (const [sis, porNumero] of Object.entries(nums)) {
    const destino = bruto[sis] || (bruto[sis] = {});
    for (const [k, valores] of Object.entries(porNumero)) {
      if (!valores.length) continue;
      if (valores.length > 1) { colisiones.push({ sistema: sis, k, opciones: valores }); continue; }
      [destino[k]] = valores;
    }
  }

  // 2) Filtro por `dependeDe`, en punto fijo: un campo entra cuando su raíz ya
  //    está resuelta (dictada ahora o respondida antes). El orden del catálogo
  //    ya pone las raíces primero, pero se repite hasta estabilizar para no
  //    depender de ese orden.
  const sistemas = {};
  let nCampos = 0;
  for (const sisId of SISTEMAS_DICTADO) {
    const candidatos = bruto[sisId];
    if (!candidatos || !Object.keys(candidatos).length) continue;
    const previos = evolucion?.sistemas?.[sisId]?.campos || {};
    const resueltos = {};
    for (let vuelta = 0; vuelta < 4; vuelta += 1) {
      let cambio = false;
      for (const [k, v] of Object.entries(candidatos)) {
        if (resueltos[k] !== undefined) continue;
        const def = defDe(sisId, k);
        if (!def) continue; // campo que ya no existe en el catálogo: se ignora
        if (!cumpleDependencia(def.dependeDe, { ...previos, ...resueltos })) continue;
        resueltos[k] = v;
        cambio = true;
      }
      if (!cambio) break;
    }
    // Lo que se quedó fuera por falta de su rama tampoco puede irse callado:
    // "orina clara, hb 8" perdía la Hb sin decir nada, porque `hb` cuelga de
    // haber declarado el laboratorio del día.
    for (const k of Object.keys(candidatos)) {
      if (resueltos[k] !== undefined) continue;
      const raiz = defDe(sisId, k)?.dependeDe?.campo;
      // Un antitrombótico SIN finalidad dicha (ni profilaxis ni anticoagulación):
      // el fármaco queda en la nota y se avisa que falta el para qué. Si la
      // profilaxis quedó escrita en este mismo parse, la finalidad ya está y no
      // hay nada que avisar.
      if (sisId === 'hemato' && k === 'anticoagulanteFarmaco' && raiz === 'anticoagulado') {
        if (resueltos.tromboprofilaxis === undefined && previos.tromboprofilaxis === undefined) fallidos.push({ sistema: sisId, k, motivo: 'finalidad', raiz: null });
        continue;
      }
      // Con su VALOR: la observación no se pierde, la ficha la conserva como
      // antecedente en la cronología (omitido 'rama') aunque no escriba el campo.
      if (raiz) fallidos.push({ sistema: sisId, k, motivo: 'rama', raiz: defDe(sisId, raiz)?.label || raiz, raizK: raiz, valor: candidatos[k] });
    }
    const n = Object.keys(resueltos).length;
    if (!n) continue;
    sistemas[sisId] = { campos: resueltos };
    nCampos += n;
  }
  // La suspensión de un sedante es estructura aunque no escriba ningún campo
  // nuevo: `retiros` viaja al merge, que cierra el fármaco.
  if (retirosNeuro.size) {
    sistemas.neuro = { ...(sistemas.neuro || { campos: {} }), retiros: { sedoanalgesia: [...retirosNeuro] } };
    nCampos += retirosNeuro.size;
  }

  // Lo que se informa al preview se recorta a lo que NO quedó escrito: si el
  // campo igual aterrizó, no se perdió nada y avisar sería ruido. Las colisiones
  // se informan aparte porque tienen otra frase (dijiste dos cosas) y su campo
  // queda a propósito en blanco.
  // Para un select, que el campo haya aterrizado significa que no se perdió
  // nada y avisar sería ruido. Para un MULTI no: la lista puede haber recibido
  // un ítem y perdido otro ("sin fiebre y hemocultivo, tomé urocultivo"
  // escribía el urocultivo y se comía el hemocultivo, mudo).
  const enSistemas = (sis, k) => defDe(sis, k)?.tipo !== 'multi'
    && sistemas[sis]?.campos?.[k] !== undefined;
  const conLabel = (x) => ({ ...x, label: defDe(x.sistema, x.k)?.label || x.k });
  const enColision = new Set(colisiones.map((c) => `${c.sistema}:${c.k}`));
  const vistos = new Set();
  const _colisiones = colisiones.filter((c) => !enSistemas(c.sistema, c.k)).map(conLabel);
  const _descartados = fallidos
      .filter((f) => !enSistemas(f.sistema, f.k) && !enColision.has(`${f.sistema}:${f.k}`))
      .filter((f) => {
        const clave = `${f.sistema}:${f.k}:${f.motivo}:${f.valor || ''}`;
        if (vistos.has(clave)) return false;
        vistos.add(clave);
        return true;
      })
      .map(conLabel);

  // Sin nada que escribir Y nada que avisar, el dictado cae entero a nota. Pero
  // una colisión o un descarte SÍ se devuelven aunque no haya ningún campo: un
  // `null` mudo es exactamente la pérdida silenciosa que este archivo se
  // prohibió, y era lo que pasaba cuando una guarda se comía la única cosa que
  // se había dicho ("clexane para la profilaxis" no mostraba nada).
  if (!nCampos && !_colisiones.length && !_descartados.length) return null;
  return { sistemas, nCampos, colisiones: _colisiones, descartados: _descartados };
}

const _vacio = (v) => v === undefined || v === null
  || (typeof v === 'string' && !v.trim())
  || (Array.isArray(v) && !v.length);

// ── El merge ──────────────────────────────────────────────────────────────────
// La evolución es la PELÍCULA, no la foto (decisión de producto 2026-08-16): su
// campo guarda el valor de AHORA. Si a las 14:00 la enfermera dice que la orina
// quedó hematúrica, negarse a escribirlo porque en la mañana decía "concentrada"
// deja un dato viejo firmado como si fuera el actual. Por eso el dictado SÍ
// reemplaza lo que había, y el reemplazo se REPORTA para que el preview lo
// muestre y nunca se aplique en silencio.
//
// Esta es la diferencia con `mergeSoportesEnEvolucion` (examenFisico.js), que
// jamás pisa una basal firmada: ese es un puente AUTOMÁTICO donde la app
// escribe sola. Acá la autora está hablando y es ella quien pide el cambio.
//
// Los `multi` (cultivos tomados, síntomas) se SUMAN en vez de reemplazar: son
// listas de cosas que pasaron, no un estado único. Nombrar un urocultivo nuevo
// no borra el hemocultivo de la mañana.
// ── El MOMENTO del hecho (candidata verificable, 2026-09-07) ─────────────────
// La evolución es la película, así que el valor vigente de un campo es el de
// la ÚLTIMA observación por el momento del HECHO, no la última que se tecleó.
// Sin esto, en el caso E de la evaluación la nota de las 23:50 ingresada a las
// 00:30 ("buena diuresis") pisaba la oliguria de las 00:10.
//
// Cómo se decide, con la doctrina de la casa (misma que el replay de la vía
// aérea y que `esPosteriorA`, estricto):
//  · `opts.momento` = el momento del hecho que se está escribiendo. Sin él (los
//    llamadores viejos y la Mesa a mano) se escribe como siempre.
//  · el libro `evolucion._dictadoLibro[sistema:campo]` recuerda qué valor
//    escribió este merge, cuándo fue el hecho y desde qué entrada. Es el libro
//    de PROCEDENCIA, como `_puenteCampos`: si el valor de hoy ya no es el que
//    el libro dice, la autora lo tocó a mano y su edición manda.
//  · una observación cuyo hecho es ANTERIOR al que ya sostiene el campo no lo
//    reemplaza: queda en la cronología como antecedente, y el reemplazo se
//    devuelve con `omitido: 'anterior'` para que la entrada lo diga.
//  · si los dos momentos no se pueden ordenar (hora desconocida, aproximada
//    muy cerca, empate) no se escribe y se devuelve `omitido:
//    'orden_incierto'`: la discrepancia se dice, jamás se elige.
// Los campos multi (listas que se suman) no compiten por vigencia.
const _claveLibro = (sis, k) => `${sis}:${k}`;
function _decidirVigencia(libro, clave, momento, valorActual, valorLibro) {
  if (!momento || !libro?.[clave]?.momento) return 'escribir';
  const propio = _mismoValor(valorActual, valorLibro);
  if (!propio) return 'escribir'; // lo tocó la autora: su valor no se defiende por momento
  const previo = libro[clave].momento;
  if (esPosteriorA(momento, previo)) return 'escribir';
  if (esPosteriorA(previo, momento)) return 'anterior';
  return 'orden_incierto';
}
const _mismoValor = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

export function mergeDictadoEnEvolucion(parseado, evolucion, opts = {}) {
  const base = evolucion && typeof evolucion === 'object' ? evolucion : { sistemas: {} };
  const entradas = Object.entries(parseado?.sistemas || {});
  if (!entradas.length) return { evolucion: base, escritos: 0, reemplazos: [], escrituras: [] };

  const momento = opts.momento && opts.momento.hora ? opts.momento : (opts.momento?.precision === 'desconocida' ? opts.momento : null);
  const libro = { ...(base._dictadoLibro || {}) };
  const sistemas = { ...(base.sistemas || {}) };
  const reemplazos = [];
  const escrituras = []; // todo lo escrito (también sobre campo vacío): la fuente de reconciliarCamposDictados
  let escritos = 0;

  for (const [sisId, patch] of entradas) {
    const prev = sistemas[sisId] || {};
    const campos = { ...(prev.campos || {}) };
    let cambio = false;
    for (const [k, v] of Object.entries(patch.campos || {})) {
      const def = defDe(sisId, k);
      if (!def) continue;
      const antes = campos[k];
      if (def.tipo === 'multi') {
        const previo = Array.isArray(antes) ? antes : [];
        const union = Array.from(new Set([...previo, ...v]));
        if (union.length === previo.length) continue; // nada nuevo que sumar
        campos[k] = union;
      } else {
        if (antes === v) continue;                    // ya dice exactamente eso
        const clave = _claveLibro(sisId, k);
        const decision = _decidirVigencia(libro, clave, momento, antes, libro[clave]?.valor);
        if (decision !== 'escribir') {
          // La observación se conserva como antecedente (en su entrada), el
          // campo sigue con la posterior, y el desacuerdo se dice.
          reemplazos.push({ sistema: sisId, k, label: def.label, antes, ahora: v, omitido: decision, vigente: antes });
          continue;
        }
        if (!_vacio(antes)) reemplazos.push({ sistema: sisId, k, label: def.label, antes, ahora: v });
        campos[k] = v;
        escrituras.push({ sistema: sisId, k, ahora: v });
        if (momento) libro[clave] = { valor: v, momento: { fecha: momento.fecha, hora: momento.hora, precision: momento.precision }, entradaId: opts.entradaId || null };
      }
      cambio = true;
      escritos += 1;
    }
    // SUSPENSIÓN DE UN SEDANTE (2026-09-08, caso L). Cierra el fármaco en la
    // lista y borra su dosis; el estado global de sedación NO se toca salvo
    // que este mismo dictado lo diga. Si la lista queda vacía y `sedacion`
    // seguía en "Con sedación" sin que nadie lo re-dijera, el campo queda SIN
    // RESPUESTA (no "Sin sedación": eso sería deducirlo) y la película avisa
    // que falta aclararlo. La cronología registra el cambio con `retirado`.
    const retirosSed = sisId === 'neuro' && Array.isArray(patch.retiros?.sedoanalgesia) ? patch.retiros.sedoanalgesia : [];
    if (retirosSed.length) {
      const previo = Array.isArray(campos.sedoanalgesia) ? campos.sedoanalgesia : [];
      const retirados = retirosSed.filter((d) => previo.includes(d));
      if (retirados.length) {
        const resto = previo.filter((d) => !retirados.includes(d));
        campos.sedoanalgesia = resto;
        if (campos.sedoDetalle && typeof campos.sedoDetalle === 'object') {
          const det = { ...campos.sedoDetalle };
          for (const d of retirados) delete det[d];
          campos.sedoDetalle = det;
        }
        const def = defDe(sisId, 'sedoanalgesia');
        const sinAclarar = !resto.length && campos.sedacion === 'Con sedación' && !(patch.campos && 'sedacion' in patch.campos);
        if (sinAclarar) delete campos.sedacion;
        reemplazos.push({ sistema: sisId, k: 'sedoanalgesia', label: def?.label || 'Sedoanalgesia', antes: previo, ahora: resto, retirado: retirados, sinAclarar });
        escrituras.push({ sistema: sisId, k: 'sedoanalgesia', ahora: resto, retirado: retirados, sinAclarar });
        cambio = true;
        escritos += retirados.length;
      }
    }
    // UNA FINALIDAD, DOS CAMPOS. Si este dictado ESCRIBE anticoagulación
    // terapéutica y el sistema tenía "tromboprofilaxis farmacológica" con el
    // mismo fármaco (o sin fármaco), la etiqueta vieja era la lectura
    // equivocada del mismo tratamiento: se retira (la parte mecánica, si la
    // había, sigue) y el cambio se dice en voz alta en la card, jamás en
    // silencio. Con fármacos DISTINTOS no se toca nada: pueden coexistir.
    if (sisId === 'hemato' && escrituras.some((w) => w.sistema === 'hemato' && w.k === 'anticoagulado' && w.ahora === 'Con anticoagulación terapéutica')) {
      // La etiqueta a retirar es la que HABÍA (si este mismo dictado dijo "no
      // es profilaxis", la cue negativa ya la movió a "Sin tromboprofilaxis":
      // eso también es la corrección, y se dice una sola vez).
      const tpAntes = (evolucion?.sistemas?.[sisId]?.campos || {}).tromboprofilaxis;
      const tpAhora = campos.tromboprofilaxis;
      const tp = (tpAhora === 'Farmacológica' || tpAhora === 'Farmacológica + mecánica') ? tpAhora
        : (tpAhora === 'Sin tromboprofilaxis' && (tpAntes === 'Farmacológica' || tpAntes === 'Farmacológica + mecánica') ? tpAntes : null);
      if (tp) {
        for (let i = reemplazos.length - 1; i >= 0; i -= 1) if (reemplazos[i].sistema === sisId && reemplazos[i].k === 'tromboprofilaxis') reemplazos.splice(i, 1);
        for (let i = escrituras.length - 1; i >= 0; i -= 1) if (escrituras[i].sistema === sisId && escrituras[i].k === 'tromboprofilaxis') escrituras.splice(i, 1);
      }
      if (tp && finalidadesIncompatibles({ ...campos, tromboprofilaxis: tp })) {
        const ahora = tp === 'Farmacológica + mecánica' ? 'Mecánica' : null;
        reemplazos.push({ sistema: sisId, k: 'tromboprofilaxis', label: defDe(sisId, 'tromboprofilaxis')?.label || 'Tromboprofilaxis', antes: tp, ahora, retirado: 'anticoagulación terapéutica' });
        if (ahora) campos.tromboprofilaxis = ahora; else delete campos.tromboprofilaxis;
        delete campos.tromboFarmaco;
        delete libro[`${sisId}:tromboprofilaxis`]; delete libro[`${sisId}:tromboFarmaco`];
        cambio = true;
      }
    }
    // Al cambiar una RAÍZ, sus hijos quedan huérfanos: con
    // `tromboprofilaxis: 'Farmacológica'` + `tromboFarmaco: 'HBPM'`, decir
    // ahora "sin tromboprofilaxis" dejaba el fármaco zombi, listo para
    // reaparecer si ella vuelve a marcar la raíz. Se limpian en punto fijo (un
    // hijo puede ser raíz de otro) y SOLO los que cuelgan de lo que este merge
    // acaba de cambiar: nada que la autora respondiera por otra vía se toca.
    const raices = new Set(reemplazos.filter((r) => r.sistema === sisId && !r.omitido).map((r) => r.k));
    for (let vuelta = 0; vuelta < 4 && raices.size; vuelta += 1) {
      const caidos = new Set();
      for (const k of Object.keys(campos)) {
        const def = defDe(sisId, k);
        const dep = def?.dependeDe?.campo;
        if (!dep || !raices.has(dep)) continue;
        if (cumpleDependencia(def.dependeDe, campos)) continue;
        delete campos[k];
        caidos.add(k);
        cambio = true;
      }
      if (!caidos.size) break;
      raices.clear();
      caidos.forEach((k) => raices.add(k));
    }
    // Campos reales anulan el "sin hallazgos" del sistema (misma regla que los
    // otros puentes: el detalle manda sobre la basal).
    if (cambio) sistemas[sisId] = { ...prev, campos, sinHallazgos: false };
  }

  if (!escritos) return { evolucion: base, escritos: 0, reemplazos, escrituras: [] };
  // El único marcador es el libro de procedencia con momento (`_dictadoLibro`),
  // y se escribe SOLO cuando el llamador trae el momento: no es un "ya sembré,
  // no vuelvo a sembrar" (la misma orina puede cambiar tres veces en un turno
  // y las tres veces la enfermera lo está diciendo), es el "quién sostiene
  // este valor y desde cuándo" que permite no pisar una observación posterior
  // con una anterior ingresada tarde.
  let salida = { ...base, sistemas };
  if (Object.keys(libro).length) salida._dictadoLibro = libro;
  // Un dato compartido dictado en un sistema (el lactato) llega lleno al otro,
  // con las mismas reglas que en la Mesa (datosCompartidos.js).
  salida = espejarCompartidos(salida, escrituras.map((e) => `${e.sistema}.${e.k}`));
  return { evolucion: salida, escritos, reemplazos, escrituras };
}

// ── Volver a derivar los campos dictados desde sus fuentes ───────────────────
// Corregir la hora de una nota que escribió campos, o borrarla, cambia qué
// observación es la última: se recorren TODAS las entradas con `datos.escritos`
// en el orden del hecho y se escribe cada campo con su última observación
// demostrable. Reglas:
//  · lo que la autora escribió a mano (el valor de hoy no es el del libro) no
//    se toca: su edición manda;
//  · dos observaciones del mismo campo que no se pueden ordenar dejan el campo
//    como está y se declara `orden_incierto` para ese campo;
//  · lo que el libro sostenía desde una entrada que ya no existe, si nadie lo
//    tocó, vuelve a la observación anterior que exista, o se vacía.
// Puro; devuelve el mismo registro si no hay nada que derivar.
function _valorDeposicionesDictadas(entrada, sistema, k, valor) {
  if (sistema !== 'gastro' || k !== 'deposiciones' || valor !== 'Presentes'
    || entrada?.tipo !== 'nota' || typeof entrada.nota !== 'string') return valor;
  // Solo se corrige la inversión demostrable del derivado desde SU nota.
  // Una mención, un plan o dos observaciones opuestas no prueban ausencia.
  return parseEvolucionDictada(entrada.nota)?.sistemas?.gastro?.campos?.deposiciones === 'Ausentes'
    ? 'Ausentes' : valor;
}

// Adapter de lectura, sin migración: solo cambia el campo derivado si aún
// coincide con el libro y la fuente exacta demuestra la inversión. No toca
// notas, escrituras históricas, horas ni campos manuales sin procedencia.
export function adaptarDeposicionesDictadas(registro) {
  const evolucion = registro?.evolucion;
  const clave = 'gastro:deposiciones';
  const propio = evolucion?._dictadoLibro?.[clave];
  const gastro = evolucion?.sistemas?.gastro;
  if (propio?.valor !== 'Presentes' || gastro?.campos?.deposiciones !== propio.valor || !propio.entradaId) return registro;
  const entrada = (Array.isArray(registro?.entradas) ? registro.entradas : []).find((e) => e.id === propio.entradaId);
  const escritura = (Array.isArray(entrada?.datos?.escritos) ? entrada.datos.escritos : [])
    .find((w) => w.sistema === 'gastro' && w.k === 'deposiciones' && w.ahora === propio.valor);
  if (!escritura || _valorDeposicionesDictadas(entrada, 'gastro', 'deposiciones', escritura.ahora) !== 'Ausentes') return registro;
  return {
    ...registro,
    evolucion: {
      ...evolucion,
      sistemas: { ...evolucion.sistemas, gastro: { ...gastro, campos: { ...gastro.campos, deposiciones: 'Ausentes' } } },
      _dictadoLibro: { ...evolucion._dictadoLibro, [clave]: { ...propio, valor: 'Ausentes' } },
    },
  };
}

export function reconciliarCamposDictados(registro) {
  const entradas = Array.isArray(registro?.entradas) ? registro.entradas : [];
  // Una observación dictada cuenta aunque NO haya escrito el campo (quedó como
  // antecedente u orden incierto en `datos.cambios`): si la posterior se borra
  // o se corrige la hora, ella puede pasar a ser la vigente; y un empate sin
  // resolver tiene que seguir avisando aunque ninguna de las dos escribiera.
  const observacionesDe = (e) => {
    const out = [];
    for (const w of (Array.isArray(e?.datos?.escritos) ? e.datos.escritos : [])) out.push({ sistema: w.sistema, k: w.k, valor: _valorDeposicionesDictadas(e, w.sistema, w.k, w.ahora) });
    for (const c of (Array.isArray(e?.datos?.cambios) ? e.datos.cambios : [])) {
      // 'rama': la observación quedó fuera porque su rama sigue cerrada; no es
      // una cuestión de orden y no se re-aplica sola (se firma lo que se ve).
      if (!c.omitido || c.omitido === 'rama' || out.some((o) => o.sistema === c.sistema && o.k === c.k)) continue;
      out.push({ sistema: c.sistema, k: c.k, valor: _valorDeposicionesDictadas(e, c.sistema, c.k, c.ahora) });
    }
    return out;
  };
  const fuentes = entradas.filter((e) => observacionesDe(e).length);
  const evo = registro?.evolucion;
  const libroPrev = evo?._dictadoLibro;
  if (!fuentes.length && !libroPrev) return { registro, discrepancias: [] };
  const ordenadas = ordenarPorHecho(fuentes, registro).map((e) => ({ e, m: momentoDeEntrada(e, registro) }));

  // Por campo: la lista de observaciones en orden del hecho.
  const porCampo = new Map();
  for (const { e, m } of ordenadas) {
    for (const w of observacionesDe(e)) {
      const clave = _claveLibro(w.sistema, w.k);
      if (!porCampo.has(clave)) porCampo.set(clave, []);
      porCampo.get(clave).push({ valor: w.valor, momento: m, entradaId: e.id });
    }
  }
  const sistemas = { ...(evo?.sistemas || {}) };
  const libro = {};
  const discrepancias = [];
  let cambio = false;
  // Lo que esta reconciliación cambió, para que un dato compartido (el
  // lactato) arrastre su copia en el otro sistema (auditoría 2026-09-29).
  const cambiados = [];
  const clavesTocadas = new Set([...porCampo.keys(), ...Object.keys(libroPrev || {})]);
  for (const clave of clavesTocadas) {
    const [sisId, k] = clave.split(':');
    const def = defDe(sisId, k);
    if (!def || def.tipo === 'multi') continue;
    const actual = sistemas[sisId]?.campos?.[k];
    const anterior = libroPrev?.[clave];
    // La autora lo cambió a mano después del último dictado: manda ella.
    if (anterior && !_mismoValor(actual, anterior.valor)) {
      // Conservar su procedencia también conserva la protección manual en
      // sucesivos replay; borrarla permitiría pisar la edición al siguiente.
      if (clave === 'gastro:deposiciones') libro[clave] = anterior;
      continue;
    }
    if (clave === 'gastro:deposiciones' && !anterior && !_vacio(actual)) continue;
    const obs = porCampo.get(clave) || [];
    if (!obs.length) {
      // Solo el libro lo sostenía y su fuente ya no está: se vacía.
      if (anterior && !_vacio(actual)) {
        const campos = { ...(sistemas[sisId]?.campos || {}) };
        delete campos[k];
        sistemas[sisId] = { ...(sistemas[sisId] || {}), campos };
        cambio = true;
        cambiados.push(`${sisId}.${k}`);
      }
      continue;
    }
    // La última demostrable: posterior a TODAS las demás con el predicado estricto.
    const ultima = obs.find((o) => obs.every((x) => x === o || esPosteriorA(o.momento, x.momento)));
    if (!ultima) {
      discrepancias.push({ tipo: 'campo_orden_incierto', sistema: sisId, campo: k, label: def.label });
      if (anterior) libro[clave] = anterior;
      continue;
    }
    libro[clave] = { valor: ultima.valor, momento: { fecha: ultima.momento.fecha, hora: ultima.momento.hora, precision: ultima.momento.precision }, entradaId: ultima.entradaId };
    if (!_mismoValor(actual, ultima.valor)) {
      const campos = { ...(sistemas[sisId]?.campos || {}), [k]: ultima.valor };
      sistemas[sisId] = { ...(sistemas[sisId] || {}), campos, sinHallazgos: false };
      cambio = true;
      cambiados.push(`${sisId}.${k}`);
    }
  }
  const libroCambio = JSON.stringify(libro) !== JSON.stringify(libroPrev || {});
  if (!cambio && !libroCambio) return { registro, discrepancias };
  let evolucion = { ...(evo || { sistemas: {} }), sistemas };
  if (Object.keys(libro).length) evolucion._dictadoLibro = libro; else delete evolucion._dictadoLibro;
  evolucion = espejarCompartidos(evolucion, cambiados);
  return { registro: { ...registro, evolucion }, discrepancias };
}

// El evento clínico ES el cambio: qué decía antes y qué dice ahora. Esta es la
// línea que queda en la CRONOLOGÍA del turno, con su hora, porque la prosa de la
// evolución no imprime la hora (norma 2026-08-02) y el campo se sobreescribe:
// sin esta entrada se perdería cuándo cambió y qué decía antes.
// Devuelve null SIN reemplazos: solo el cambio merece su línea, y la decisión
// vive acá (pura y testeable) en vez de en el handler de la UI. Catálogo
// cerrado, cero texto del ASR; el preview pinta los mismos datos con su flecha,
// que es SOLO presentación.
// El valor ANTERIOR no lo escribió este parser: sale del jsonb almacenado, y
// esta es la ÚNICA puerta por la que un valor guardado se copia a un registro
// nuevo y persistido. Por eso se re-valida AL LEER contra el catálogo, misma
// disciplina que `MEDIDA_RE` en `fraseLesion` (#642): un jsonb adulterado jamás
// imprime. Sin esto, un `campos.orina` con texto libre (o con PII) aterrizaría
// en la cronología sin pasar por ninguna guarda.
const NUM_SUELTO = /^-?\d+(\.\d+)?$/;
const _imprimible = ({ sistema, k, antes, ahora, retirado, omitido }) => {
  const def = defDe(sistema, k);
  if (!def) return false;
  // Suspensión de un multi: lo retirado y lo que sigue son opciones del catálogo.
  if (Array.isArray(retirado)) return retirado.every((x) => (def.opciones || []).includes(x)) && (Array.isArray(ahora) ? ahora : []).every((x) => (def.opciones || []).includes(x));
  if (retirado) return (def.opciones || []).includes(antes);
  // Observación que no escribió por rama cerrada: solo trae `ahora`.
  if (omitido === 'rama') return (def.opciones || []).includes(ahora);
  if (def.tipo === 'num') return NUM_SUELTO.test(String(antes)) && NUM_SUELTO.test(String(ahora));
  return (def.opciones || []).includes(antes) && (def.opciones || []).includes(ahora);
};

// Lo que NO se escribió se dice con todas sus letras, sin reproche: ella tiene
// que poder ver que faltó algo. Vive acá y no en la UI porque los motivos son el
// catálogo cerrado que produce el parser, y así se pueden fijar por test.
// Label visible de un campo del catálogo, para avisos armados fuera de este
// archivo (la cascada de dictadoCascada.js descarta con el mismo vocabulario).
export const labelCampoDictado = (sis, k) => defDe(sis, k)?.label || k;
// Y su dependencia de rama, para que esa cascada pueda aplicar su regla
// cronológica también a los campos que la sombra de una acción dejó sin rama.
export const dependeDeCampoDictado = (sis, k) => defDe(sis, k)?.dependeDe || null;

// El `canal` solo cambia el VERBO de la frase: desde el 2026-08-29 la misma
// captura llega por micrófono o por teclado, y decirle "lo dijiste" a quien lo
// escribió suena a que la app no la escuchó. Default intacto: los llamadores
// que no lo pasan siguen leyéndose igual.
export function avisoDescarte({ label, valor, motivo, raiz, nuevo }, { canal = 'dictado' } = {}) {
  const que = `${label}${valor ? ` (${valor})` : ''}`;
  const loDijiste = canal === 'texto' ? 'lo escribiste' : 'lo dijiste';
  const elRelato = canal === 'texto' ? 'lo que escribiste' : 'el mismo dictado';
  if (motivo === 'rango') return `No anoté ${que}: quedó fuera de lo que puedo dar por bueno. Escríbelo tú si es correcto.`;
  if (motivo === 'sujeto') return `No anoté ${que}: no quedó claro de cuál valor era ese número.`;
  if (motivo === 'negado') return `No anoté ${que}: en esa frase iba en negativo.`;
  // La duda se dice como duda: afirmar "iba en negativo" cuando el parser
  // justamente NO supo sería mentirle ("sin dolor y orina clara" no niega la
  // orina).
  if (motivo === 'dudoso') return `No anoté ${que}: no supe si eso iba en negativo. Escríbelo tú.`;
  if (motivo === 'programado') return `No anoté ${que}: no supe si eso ya pasó o está por pasar. Escríbelo tú.`;
  if (motivo === 'retirado') return `No anoté ${que}: ${loDijiste} como retirado o suspendido.`;
  // La suspensión de un sedante que la Mesa no tenía registrado: no hay qué cerrar.
  if (motivo === 'no_registrado') return `No anoté la suspensión de ${String(valor || '').toLowerCase()}: no estaba registrado en sedoanalgesia. Queda en tu nota tal cual.`;
  if (motivo === 'plan') return `No anoté ${que}: ${loDijiste} como algo por hacer, no como algo que pasó.`;
  if (motivo === 'rama') return `No anoté ${que}: para eso falta responder ${String(raiz || '').toLowerCase()}.`;
  // La finalidad de un anticoagulante no se deduce del nombre ni de la dosis.
  if (motivo === 'finalidad') return `No anoté la finalidad del anticoagulante: no dijiste si es profilaxis o anticoagulación. El fármaco queda en tu nota tal cual.`;
  // El relato se cuenta en orden y el orden es la cronología: un valor dicho
  // ANTES de una acción que lo cambió es el estado previo, no el de ahora.
  if (motivo === 'superado') return `No anoté ${que}: ${elRelato} lo deja después en ${String(nuevo || '').toLowerCase()}.`;
  return `No anoté ${que}.`;
}

export function notaCambioDictado(reemplazos) {
  const frases = (reemplazos || []).filter(_imprimible).map(({ label, antes, ahora, omitido, retirado, sinAclarar, raiz }) => {
    // Suspensión de un fármaco de sedoanalgesia: qué se suspende y qué sigue;
    // si no sigue ninguno, el estado de sedación queda por aclarar (se dice,
    // no se deduce).
    if (Array.isArray(retirado)) {
      const sigue = (Array.isArray(ahora) ? ahora : []).map((x) => x.toLowerCase());
      return `${label}: se suspende ${retirado.map((x) => x.toLowerCase()).join(' y ')}${sigue.length ? `; sigue ${sigue.join(', ')}` : (sinAclarar ? '; estado de sedación por aclarar' : '')}`;
    }
    // Observación que NO escribió porque su rama sigue cerrada en la Mesa
    // (vigil con sedación registrada): se conserva como antecedente y se dice.
    if (omitido === 'rama') return `${label}: se observó ${String(ahora).toLowerCase()}; no se anotó porque ${String(raiz || 'su rama').toLowerCase()} sigue como estaba en la Mesa, revísalo`;
    // La etiqueta que se retira por una corrección explícita se dice con su
    // porqué (una finalidad no puede quedar escrita dos veces distinta).
    if (retirado) return `${label}: antes ${String(antes).toLowerCase()}; se retira, es ${retirado}${ahora ? ` (queda ${String(ahora).toLowerCase()})` : ''}`;
    // La observación que NO reemplazó al campo se dice como lo que es: un
    // antecedente (su hecho es anterior al que sostiene el campo) o una duda
    // de orden. El campo sigue diciendo lo posterior.
    if (omitido === 'anterior') return `${label}: se observó ${String(ahora).toLowerCase()}; se conserva la observación posterior (${String(antes).toLowerCase()})`;
    if (omitido === 'orden_incierto') return `${label}: se observó ${String(ahora).toLowerCase()}, sin poder ordenarla con la que dice ${String(antes).toLowerCase()}; revisa la hora`;
    return `${label}: antes ${String(antes).toLowerCase()}, ahora ${String(ahora).toLowerCase()}`;
  });
  return frases.length ? frases.join('; ') : null;
}

// ── "Así queda" ───────────────────────────────────────────────────────────────
// El preview sale de la MISMA función que imprime el documento
// (`compilarSistemaDetalle` recortada a los grupos tocados, igual que la línea
// viva del chequeo general): preview y documento no pueden divergir.
export function lineasDictadoEvolucion(parseado, evolucion) {
  const { evolucion: next } = mergeDictadoEnEvolucion(parseado, evolucion);
  const out = [];
  for (const [sisId, patch] of Object.entries(parseado?.sistemas || {})) {
    const grupos = Array.from(new Set(
      Object.keys(patch.campos || {}).map((k) => defDe(sisId, k)?.grupo).filter(Boolean),
    ));
    const st = next.sistemas?.[sisId] || {};
    const { lineas } = compilarSistemaDetalle(sisId, st.campos || {}, st.escalas || {}, { grupos });
    if (lineas.length) {
      out.push({ sistema: sisId, label: SISTEMAS.find((s) => s.id === sisId)?.label || sisId, lineas });
    }
  }
  return out;
}

// ── Qué quedó fuera de este parser, y por qué ────────────────────────────────
// Documentado en código para que la próxima ola no lo tome por olvido:
// · Campos `tipo: 'text'` (esquema ATB, fórmula de NE, corrección HE, motivo de
//   la falta de tromboprofilaxis…): el ASR crudo JAMÁS entra a un campo. Un
//   campo de texto solo podría llenarse con el transcrito, así que ninguno se
//   dicta. Es la frontera dura del módulo, no una limitación temporal.
// · `plaquetas`, `gb` y `ran`: la unidad es ×10³/µL y se dicen indistintamente
//   "90" o "90 mil". El rango plausible NO desambigua (las dos formas caen
//   dentro), así que se quedan fuera hasta que la unidad se diga entera.
// · `gb` además vive en DOS sistemas (infecto y hemato) con el mismo nombre:
//   sin una desambiguación explícita, adivinar sistema sería peor que no caer.
// · `abdomen`, `rha` y `bristol` de gastro: son hallazgos de EXAMEN y ya tienen
//   su parser (parseExamenDictado) en su superficie; duplicarlos acá crearía dos
//   puertas para el mismo dato.
// · `foco` de infecto: "foco urinario" y la vía urinaria de renal comparten
//   vocabulario; entra cuando haya evidencia de uso real.
export const FUERA_DE_ALCANCE = ['text', 'plaquetas', 'gb', 'ran', 'abdomen', 'rha', 'bristol', 'foco'];

// Kill-switch ON por defecto, como toda pieza nueva de la casa.
export function evolucionDictadaHabilitada() {
  return process.env.REACT_APP_EVOLUCION_VOZ_ENABLED !== 'false';
}
